import axios from "axios";
import localforage from "localforage";
import { supabase, auth } from "../../supabase/client";
import { mergeClubQueryParams } from "../clubScope";
import tokenManager, { AUTH_FAILED_FLAG } from "../security/tokenManager";
import {
  billingPeriodFormToApiPayload,
  normalizeBillingPeriodFromApi,
} from "../../comp/admin/clubSettings/billingPeriodUtils";
import {
  clubGroupTypeFormToApiPayload,
  normalizeClubGroupTypeFromApi,
} from "../../comp/admin/clubSettings/groupTypeUtils";
import { filterSelectableMembers } from "../memberArchiveUtils";
import {
  maybeOfferSupportErrorFromApiFailure,
  installSupportErrorReportAxiosInterceptor,
} from "../support/supportErrorReportFromApi";
import { installClientDiagnosticsBuffer } from "../support/clientDiagnosticsBuffer";

installClientDiagnosticsBuffer();

// Create axios instance with timeout configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_CONNECTION_API,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

export { apiClient };

let recoverPromise = null;
let authFailureRedirectStarted = false;

function bearerFromConfig(config) {
  const headers = config?.headers;
  if (!headers) return "";
  const raw = typeof headers.get === "function"
    ? headers.get("Authorization")
    : (headers.Authorization || headers.authorization || "");
  return String(raw || "").replace(/^Bearer\s+/i, "").trim();
}

function isTokenRejection(error) {
  if (error.response?.status !== 401) return false;
  const data = error.response?.data;
  const code = String(data?.code || "");
  const msg = String(data?.error || data?.message || "").toLowerCase();
  if (code === "invalid_token") return true;
  return (
    msg.includes("invalid token") ||
    msg.includes("no token") ||
    msg.includes("token expired") ||
    msg.includes("jwt")
  );
}

async function recoverAccessToken(failedToken) {
  if (!recoverPromise) {
    recoverPromise = (async () => {
      try {
        const { data, error } = await auth.getSession();
        let next = !error ? data?.session?.access_token || null : null;
        if (!next || next === failedToken) {
          const refreshed = await auth.refreshSession();
          next = refreshed.data?.session?.access_token || null;
        }
        if (!next || next === failedToken) return null;
        return tokenManager.setToken(next) ? next : null;
      } catch {
        return null;
      } finally {
        recoverPromise = null;
      }
    })();
  }
  return recoverPromise;
}

async function forceLoginRedirect() {
  if (authFailureRedirectStarted) return;
  authFailureRedirectStarted = true;
  try {
    sessionStorage.setItem(AUTH_FAILED_FLAG, "1");
  } catch {
    /* ignore */
  }
  try {
    await Promise.race([
      auth.signOut({ scope: "local" }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch {
    /* session may already be gone */
  }
  tokenManager.clearTokens();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
}

// Add request interceptor for auth headers
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    // Stale copied token: refresh the Supabase session once and retry.
    // If the token is still rejected, sign out fully so /login cannot
    // bounce straight back into the app.
    if (isTokenRejection(error) && original && !original.__authRetried) {
      original.__authRetried = true;
      const nextToken = await recoverAccessToken(bearerFromConfig(original));
      if (nextToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${nextToken}`;
        return apiClient(original);
      }
      await forceLoginRedirect();
      return Promise.reject(error);
    }
    if (isTokenRejection(error)) {
      await forceLoginRedirect();
      return Promise.reject(error);
    }
    
    // Handle 403 Forbidden - Cross-club access attempt
    if (error.response?.status === 403) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.error || errorData?.message || 'Access denied. You do not have permission to access this data.';
      error.message = errorMessage;
      // Optionally show user-friendly error
      if (errorMessage.includes('club') || errorMessage.includes('Club')) {
        error.clubError = true;
      }
      return Promise.reject(error);
    }
    
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      error.message = 'Request timeout - please check your connection and try again';
    }

    maybeOfferSupportErrorFromApiFailure(error);
    return Promise.reject(error);
  }
);

// Standalone `axios` calls (board, profile upload, etc.) — same support offer as apiClient.
installSupportErrorReportAxiosInterceptor(axios);

// Helper: Get Authorization header for all requests
function getAuthHeaders() {
  const token = tokenManager.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// --- All API Calls ---

export const fetchMembers = async () => {
  const response = await apiClient.get(
    `/api/members`,
    {
      headers: { ...getAuthHeaders() }
    }
  );
  return filterSelectableMembers(response.data);
};

export const fetchUnreviewedMembers = async () => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  try {
    const response = await apiClient.get(
      `/api/members/unreviewed`,
      {
        headers: { ...getAuthHeaders() }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching unreviewed members:', error.message);
    return [];
  }
};

export const deletePendingUser = async (studentID) => {
  const response = await apiClient.delete(
    `/api/pending-user/${studentID}`,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

/** Signup: check personnummer before a login is created. */
export const validateSignupPersonnummer = async (personnummer) => {
  const response = await apiClient.post(
    `/api/signup/validate-personnummer`,
    { personnummer },
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data;
};

/** Signup: save club application. An unfinished login is removed if this save fails. */
export const saveSignupPendingProfile = async (profileData) => {
  const response = await apiClient.post(
    `/api/signup/pending-profile`,
    profileData,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } }
  );
  return response.data;
};

/** Logged-in user: check if signup is still in pending_users (no users row yet). */
export const fetchMyRegistrationStatus = async () => {
  const response = await apiClient.get(
    `/api/signup/my-registration-status`,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

/** Public club list for signup (no auth). */
export const fetchSignupClubs = async () => {
  const response = await apiClient.get(
    `/api/public/clubs`
  );
  return response.data?.clubs || [];
};

/** Admin: signed opaque tokens for club signup invite links. */
export const fetchClubSignupInvites = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/club-settings/signup-invites`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

/** Staff: signed token for the club public anmälan landing page. */
export const fetchAnmalningarPublicLink = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/anmalningar/public-link`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

/** Public: resolve ?i= token to club + visible registration groups (no auth). */
export const fetchPublicAnmalningLanding = async (token) => {
  const response = await apiClient.get(
    `/api/public/anmalningar/landing`,
    { params: { i: token } }
  );
  return response.data;
};

export const fetchAnmalningQuestionnaires = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/anmalningar/questionnaires`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const saveAnmalningQuestionnaire = async (id, body, clubID = null) => {
  const response = await apiClient.put(
    `/api/anmalningar/questionnaires/${encodeURIComponent(id)}`,
    body,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const fetchAnmalningConsentSettings = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/anmalningar/consent-settings`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const saveAnmalningConsentSettings = async (body, clubID = null) => {
  const response = await apiClient.put(
    `/api/anmalningar/consent-settings`,
    body,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const fetchPendingAnmalningSubmissionsCount = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/anmalningar/submissions/pending-count`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const fetchAnmalningSubmissions = async ({ status = "pending", clubID = null } = {}) => {
  const response = await apiClient.get(
    `/api/anmalningar/submissions`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({ status }, clubID) }
  );
  return response.data;
};

export const updateAnmalningSubmission = async (id, body, clubID = null) => {
  const response = await apiClient.patch(
    `/api/anmalningar/submissions/${encodeURIComponent(id)}`,
    body,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const approveAnmalningSubmission = async (id, body = {}, clubID = null) => {
  const response = await apiClient.post(
    `/api/anmalningar/submissions/${encodeURIComponent(id)}/approve`,
    body,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const fetchPublicAnmalningApplyForm = async (token, groupId, lang = null) => {
  const params = { i: token, groupId };
  if (lang) params.lang = String(lang).slice(0, 2);
  const response = await apiClient.get(
    `/api/public/anmalningar/apply-form`,
    { params }
  );
  return response.data;
};

export const submitPublicAnmalningApplication = async (body) => {
  const response = await apiClient.post(
    `/api/public/anmalningar/submit`,
    body
  );
  return response.data;
};

export const fetchClubExpenses = async (clubID = null) => {
  const response = await apiClient.get(
    `/api/club-expenses`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const fetchAdHocInvoices = async () => {
  const response = await apiClient.get(`/api/finance/ad-hoc-invoices`, {
    headers: { ...getAuthHeaders() },
  });
  return response.data;
};

export const createAdHocInvoice = async (payload) => {
  const response = await apiClient.post(`/api/finance/ad-hoc-invoices`, payload, {
    headers: { ...getAuthHeaders() },
  });
  return response.data;
};

export const downloadAdHocInvoicePdf = async (id, locale = "sv-SE") => {
  const response = await apiClient.get(`/api/finance/ad-hoc-invoices/${encodeURIComponent(id)}/pdf`, {
    headers: { ...getAuthHeaders() },
    params: { locale },
    responseType: "blob",
  });
  return response.data;
};

export const fetchAdHocInvoiceSenderProfile = async () => {
  const response = await apiClient.get(`/api/finance/ad-hoc-invoices/sender-profile`, {
    headers: { ...getAuthHeaders() },
  });
  return response.data;
};

export const previewAdHocInvoicePdf = async (payload) => {
  const response = await apiClient.post(`/api/finance/ad-hoc-invoices/preview`, payload, {
    headers: { ...getAuthHeaders() },
    responseType: "blob",
  });
  return response.data;
};

export const deleteAdHocInvoice = async (id) => {
  const response = await apiClient.delete(`/api/finance/ad-hoc-invoices/${encodeURIComponent(id)}`, {
    headers: { ...getAuthHeaders() },
  });
  return response.data;
};

export const fetchClubEconomySummary = async ({ period = "30d", from, to, clubID = null } = {}) => {
  const params = mergeClubQueryParams({ period }, clubID);
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await apiClient.get(
    `/api/club-economy/summary`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return response.data;
};

export const createClubExpense = async (payload, receiptFile = null, clubID = null) => {
  const formData = new FormData();
  formData.append("name", payload.name);
  formData.append("expenseDate", payload.expenseDate);
  formData.append("amount", String(payload.amount));
  if (payload.currency) formData.append("currency", payload.currency);
  if (payload.source) formData.append("source", payload.source);
  if (receiptFile) formData.append("receipt", receiptFile);
  const response = await apiClient.post(
    `/api/club-expenses`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const updateClubExpense = async (id, payload, receiptFile = null, clubID = null) => {
  const formData = new FormData();
  formData.append("name", payload.name);
  formData.append("expenseDate", payload.expenseDate);
  formData.append("amount", String(payload.amount));
  if (payload.currency) formData.append("currency", payload.currency);
  if (payload.source) formData.append("source", payload.source);
  if (receiptFile) formData.append("receipt", receiptFile);
  const response = await apiClient.patch(
    `/api/club-expenses/${id}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const deleteClubExpense = async (id, clubID = null) => {
  await apiClient.delete(`/api/club-expenses/${id}`, {
    headers: { ...getAuthHeaders() },
    params: mergeClubQueryParams({}, clubID),
  });
};

/** Public: resolve ?i= token to club + role (no auth). */
export const resolvePublicSignupInvite = async (token) => {
  const response = await apiClient.get(
    `/api/public/signup-invite`,
    { params: { i: token } }
  );
  return response.data;
};

export const fetchAllUnpublishedBeltPromotions = async () => {
   const response = await apiClient.get(
    `/api/unpublishedbeltpromotions`,
     {
       headers: { ...getAuthHeaders() }
     }
  );
   return response.data;
};

export const setHasParent = async (parentID, studentID) => {
  return { success: true };
};

export const setHasSecondParent = async (parentID, studentID) => {
  const response = await apiClient.put(
    `/api/set-has-second-parent`,
    { parentID, studentID },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const clearLocalStorage = () => {
  localStorage.removeItem("club");
  localStorage.removeItem("email");
  localStorage.removeItem("name");
  localforage.removeItem("userInfo");
  localforage.removeItem("activeUser");
};

export const addBeltPromotion = async (studentID, name, email, belt, beltStripe, groupID, promotor, promotionDate) => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  if (!studentID || !name || !belt)
    throw new Error("Missing required fields: studentID, name, or belt");

  try {
    const requestBody = { 
      studentID, 
      name, 
      email, 
      belt, 
      beltStripe, 
      groupID, 
      promotor, 
      promotionDate 
    };

    const response = await apiClient.post(
      `/api/add-belt-promotion`,
      requestBody,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error adding belt promotion:', error.message);
    throw error;
  }
};

export const addBeltPromotionBatch = async (studentID, name, email, belt, beltStripe, groupID, promotor, promotionDate) => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  if (!studentID || !name || !belt)
    throw new Error("Missing required fields: studentID, name, or belt");
  
  try {
    const requestBody = { 
      studentID, 
      name, 
      email, 
      belt, 
      beltStripe, 
      groupID, 
      promotor, 
      promotionDate 
    };
    
    const response = await apiClient.post(
      `/api/add-belt-promotion-batch`,
      requestBody,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error adding belt promotion batch:', error.message);
    throw error;
  }
};

export const updateBeltPromotion = async (
  studentID, name, email, belt, beltStripe, promotor, promotionDate, promotionID = null, beltPromotionId = null
) => {
  // Backend expects beltAwarded and stripeAwarded
  // beltPromotionId: for unpublished belt_promotions (BeltPromotionPage)
  // promotionID: for published student_promotions
  const requestBody = {
    studentID: studentID ?? null,
    beltAwarded: belt,
    stripeAwarded: beltStripe ?? 0,
    promotor: promotor ?? null,
    promotionDate: promotionDate ?? null,
  };
  
  if (beltPromotionId != null) {
    requestBody.beltPromotionId = beltPromotionId;
  } else if (promotionID) {
    requestBody.promotionID = promotionID;
  }
  
  if (!beltPromotionId && (!studentID || !name || !belt))
    throw new Error("Missing required fields: studentID, name, or belt (or beltPromotionId for unpublished)");

  const response = await apiClient.post(
    `/api/update-belt-promotion`,
    requestBody,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const deleteBeltPromotion = async (studentID) => {
  if (!studentID)
    throw new Error("Missing required fields: studentID or belt");

  const response = await apiClient.post(
    `/api/delete-belt-promotion`,
    { studentID },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
}

export const deleteBeltPromotionUser = async (promotionID) => {
  if (!promotionID)
    throw new Error("Missing required fields: studentID or belt");
   const response = await apiClient.post(
     `/api/delete-belt-promotion-user`,
     { promotionID },
     { headers: { ...getAuthHeaders() } }
   );
   return response.data;
}

export const publishBeltPromotionAPI = async (
  studentID, belt, beltStripe, promotor, promotionDate
) => {
  if (!studentID || !belt)
    throw new Error("Missing required fields: studentID or belt");

  const response = await apiClient.post(
    `/api/update-user-and-add-promotion`,
     { studentID, belt, beltStripe, promotor, promotionDate },
     { headers: { ...getAuthHeaders() } }
   );
   return response.data;
};

export const batchPublishBeltPromotions = async (promotions) => {
  if (!Array.isArray(promotions) || promotions.length === 0) {
    throw new Error("promotions must be a non-empty array");
  }

  const response = await apiClient.post(
    `/api/batch-publish-belt-promotions`,
    { promotions },
    { 
      headers: { ...getAuthHeaders() },
      timeout: 120000 // 2 minute timeout for large batches
    }
  );
  
  return response.data;
};

export const fetchPendingBeltPromotions = async (clubID) => {
  try {
    const response = await apiClient.get(
      `/api/pending-belt-promotions/count`,
      { 
        params: clubID ? { clubID } : {},
        headers: { ...getAuthHeaders() } 
      }
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchPendingColorStripePromotions = async () => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  try {
    const response = await apiClient.get(
      `/api/pending-color-stripe-promotions/count`,
      { 
        headers: { ...getAuthHeaders() } 
      }
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const deletePendingColorStripePromotion = async (promotionID) => {
  if (!promotionID) throw new Error("Missing required field: promotionID");
  
  try {
    const response = await apiClient.post(
      `/api/delete-pending-color-stripe-promotion`,
      { promotionID },
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchMembersCount = async () => {
  return { count: 0 };
};

export const getAllUsers = async (studentId, email) => {
  if (!studentId || !email) throw new Error('Both studentId and email are required');
  return [];
};

export const getAllUsersImage = async (studentId, email) => {
  if (!studentId || !email) throw new Error('Both studentId and email are required');
  return []; // Temporary return for migration
};

export const getAllNonCoachUsers = async (studentId, email) => {
  if (!studentId || !email) throw new Error('Both studentId and email are required');
  const response = await apiClient.get(
    `/api/all-non-coach-users`,
    { params: { studentId, email }, headers: { ...getAuthHeaders() } }
  );
  return filterSelectableMembers(response.data);
};

export const getAllCoaches = async () => {
  const response = await apiClient.get(
    `/api/coaches`,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const getAllUsersBySearchTerm = async (term, studentId, email) => {
  const response = await apiClient.get(
    `/api/users/search`,
    { 
      params: { term, studentId, email }, 
      headers: { ...getAuthHeaders() }
    }
  );
  return filterSelectableMembers(response.data);
};

export const getStudentsByCoach = async (coachID, studentId, email) => {
  const response = await apiClient.get(
    `/api/coaches/${coachID}/students`,
    { params: { studentId, email }, headers: { ...getAuthHeaders() } }
  );
  return filterSelectableMembers(response.data);
};

export const setUserAsCoach = async (studentID, email) => {
  if (!studentID || !email) throw new Error('Both studentID and email are required');
  const response = await apiClient.post(
    `/api/set-as-coach`,
    { studentID, email },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const setBoardMemberFlag = async (studentID, isBoardMember, clubID = null, boardRole = null) => {
  const response = await apiClient.post(
    `/api/set-board-member`,
    {
      studentID,
      isBoardMember,
      boardRole: isBoardMember ? boardRole : null,
    },
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const removeAsCoach = async (studentID, email) => {
  if (!studentID || !email) throw new Error('Both studentID and email are required');
  const response = await apiClient.post(
    `/api/remove-as-coach`,
    { studentID, email },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const setAsStudent = async (studentID, coachID) => {
  const response = await apiClient.post(
    `/api/set-as-student`,
    { studentID, coachID },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const removeAsStudent = async (studentID, coachID) => {
  const response = await apiClient.post(
    `/api/remove-as-student`,
    { studentID, coachID },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const fetchAllChildPromotions = async (clubID) => {
  try {
    const response = await apiClient.get(
      `/api/child-promotions`,
      { 
        params: clubID ? { clubID } : {},
        headers: { ...getAuthHeaders() } 
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchAllAdultPromotions = async () => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  try {
    const response = await apiClient.get(
      `/api/promotions`,
      { 
        headers: { ...getAuthHeaders() } 
      }
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.promotions && Array.isArray(data.promotions)) return data.promotions;
    return [];
  } catch (error) {
    throw error;
  }
};

export const approvePromotions = async (promotions) => {
  if (!Array.isArray(promotions) || promotions.length === 0) {
    throw new Error("Promotions array is required");
  }
  const response = await apiClient.post(
    `/api/approve-promotions`,
    { promotions },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const deleteAdultPromotion = async (promotionID) => {
  if (!promotionID) throw new Error("Missing required field: promotionID");
  
  try {
    const response = await apiClient.post(
      `/api/delete-adult-promotion`,
      { promotionID },
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchUserByEmail = async (email) => {
  try {
    const response = await apiClient.get(
      `/api/user?email=${encodeURIComponent(email)}`,
      { headers: { ...getAuthHeaders() } }
    );
    const data = response.data;
    if (Array.isArray(data)) return filterSelectableMembers(data);
    if (data && typeof data === "object") return filterSelectableMembers([data]);
    return [];
  } catch (error) {
    throw error;
  }
};

/** Lookup parent by email for adding co-parent - returns parent(s) with children (name, image) */
export const lookupParentByEmail = async (email) => {
  try {
    const response = await apiClient.get(
      `/api/user/lookup-parent?email=${encodeURIComponent(email)}`,
      { headers: { ...getAuthHeaders() } }
    );
    const data = response.data;
    if (Array.isArray(data)) return filterSelectableMembers(data);
    if (data && typeof data === "object") return filterSelectableMembers([data]);
    return [];
  } catch (error) {
    throw error;
  }
};

export const fetchMainUserByEmail = async (email) => {
  try {
    const url = `/api/user?email=${encodeURIComponent(email)}&_t=${Date.now()}`;
    
    const response = await apiClient.get(
      url,
      { headers: { ...getAuthHeaders() } }
    );
    
    // Convert single user object to array format expected by frontend
    let result;
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      result = [response.data];
    } else {
      result = response.data;
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};

export const fetchChildUserByEmail = async (email) => {
  return [];
};

export const fetchFamilyByID = async (studentID) => {
  // const response = await apiClient.get(
  //   `/api/family/${studentID}`,
  //   { headers: { ...getAuthHeaders() } }
  // );
  // return response.data;
  return { data: [] }; // Temporary return for migration - return object with data property
};

export const fetchPromotionsByStudentID = async (studentID) => {
  try {
    const response = await apiClient.get(
      `/api/student/${studentID}/promotions`,
      { headers: { ...getAuthHeaders() } }
    );
    
    return response.data || [];
  } catch (error) {
    throw error;
  }
};

export const fetchMembersWithBeltPromotions = async () => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  try {
    const response = await apiClient.get(
      `/api/members-with-belt-promotions`,
      { 
        headers: { ...getAuthHeaders() } 
      }
    );
    
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.members && Array.isArray(data.members)) return data.members;
    return [];
  } catch (error) {
    throw error;
  }
};

export const fetchUserByStudentID = async (studentID) => {
  try {
    const response = await apiClient.get(
      `/api/users/${encodeURIComponent(studentID)}?_t=${Date.now()}`,
      { headers: { ...getAuthHeaders() } }
    );
    
    // Return the user data directly (should be a single user object)
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Admin-only activity timeline for a member (UserDetailDrawer). */
export const fetchUserActivityLog = async (studentID, limit = 100) => {
  try {
    const response = await apiClient.get(
      `/api/users/${encodeURIComponent(studentID)}/activity-log`,
      {
        headers: { ...getAuthHeaders() },
        params: { limit },
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw error;
  }
};

export const fetchAllGroups = async (clubID = null) => {
  try {
    const params = clubID != null ? { clubID } : {};
    const response = await apiClient.get(
      `/api/groups`,
      { params, headers: getAuthHeaders() }
    );
    const { normalizeClassGroupFromApi } = await import("../classGroupFields");
    const rows = response.data || [];
    return Array.isArray(rows) ? rows.map(normalizeClassGroupFromApi) : [];
  } catch (error) {
    console.error('Error in fetchAllGroups:', error);
    return [];
  }
};

// ---------- Finance API (backend enforces clubID from JWT) ----------
/** After a 404 on finance routes, skip further GETs this session (quiet mock mode). Clear: sessionStorage.removeItem(key). */
const FINANCE_HTTP_SKIP_KEY = "mmacenter:financeHttpUnavailable:v1";

/** Must match `FINANCE_SELECTED_PERIOD_STORAGE_KEY` in `FinanceBillingContext.jsx`. */
const FINANCE_SELECTED_PERIOD_STORAGE_KEY = "mmacenter:ekonomi:financeSelectedPeriodId:v1";

function readStoredFinanceSelectedPeriodId() {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage?.getItem(FINANCE_SELECTED_PERIOD_STORAGE_KEY);
    return v && typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

/** Set `REACT_APP_FINANCE_USE_SERVER=false` to avoid calling `/api/finance/*` when the deployed API has no ekonomi routes. */
export function isFinanceServerDisabledByEnv() {
  return String(process.env.REACT_APP_FINANCE_USE_SERVER || "").toLowerCase() === "false";
}

function readFinanceHttpSkippedStorage() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(FINANCE_HTTP_SKIP_KEY) === "1";
  } catch {
    return false;
  }
}

export function isFinanceHttpSkipped() {
  return isFinanceServerDisabledByEnv() || readFinanceHttpSkippedStorage();
}

/** Clear session flag that blocks finance GETs (e.g. after a transient 404). */
export function resetFinanceApiSession() {
  clearFinanceHttpSkipped();
}

function markFinanceHttpSkipped() {
  try {
    if (typeof window !== "undefined") window.sessionStorage.setItem(FINANCE_HTTP_SKIP_KEY, "1");
  } catch {
    /* ignore */
  }
}

function clearFinanceHttpSkipped() {
  try {
    if (typeof window !== "undefined") window.sessionStorage.removeItem(FINANCE_HTTP_SKIP_KEY);
  } catch {
    /* ignore */
  }
}

function isFinanceRouteMissingError(err) {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
}

function financeApiErrorMessage(err, fallback = "Request failed") {
  const data = err?.response?.data;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

/**
 * @template T
 * @param {string} url
 * @param {object} config
 * @param {T} fallbackValue
 */
async function financeGetJson(url, config, fallbackValue) {
  if (isFinanceHttpSkipped()) return fallbackValue;
  try {
    const res = await apiClient.get(url, config);
    return res.data !== undefined ? res.data : fallbackValue;
  } catch (err) {
    return fallbackValue;
  }
}

/** Same roster source as `/api/finance/members` when ekonomi routes are off (reviewed users only). */
function normalizeMemberGroupIDs(m) {
  let g = m.groupIDs;
  if (typeof g === "string") {
    try {
      const parsed = JSON.parse(g);
      g = Array.isArray(parsed) ? parsed : [];
    } catch {
      g = [];
    }
  }
  if (!Array.isArray(g)) g = [];
  const nums = g
    .map((x) => (typeof x === "number" ? x : parseInt(String(x), 10)))
    .filter((n) => !Number.isNaN(n));
  if (nums.length === 0 && m.groupID != null) {
    const one = typeof m.groupID === "number" ? m.groupID : parseInt(String(m.groupID), 10);
    if (!Number.isNaN(one)) nums.push(one);
  }
  return nums;
}

function mapClubUsersToFinanceRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((u) => {
      if (!u || u.studentID == null) return false;
      const sid = typeof u.studentID === "number" ? u.studentID : parseInt(String(u.studentID), 10);
      if (Number.isNaN(sid)) return false;
      if (u.reviewed !== true) return false;
      const isStudent = u.isStudent === true || u.is_student === true;
      const isParent = u.isParent === true || u.is_parent === true;
      if (isStudent) return true;
      if (isParent && !isStudent) return false;
      return true;
    })
    .map((m) => {
      const studentID = typeof m.studentID === "number" ? m.studentID : parseInt(String(m.studentID), 10);
      const groupIDs = normalizeMemberGroupIDs(m);
      const primaryGroupId = groupIDs.length > 0 ? groupIDs[0] : null;
      return {
        studentID,
        name: m.name,
        email: m.email || "",
        belt: m.belt ?? null,
        beltStripe: m.beltStripe ?? m.belt_stripe ?? null,
        lastPromotionDate: m.lastPromotionDate ?? m.last_promotion_date ?? null,
        groupIDs,
        groupID: primaryGroupId,
        groupName: null,
        expectedPrice: null,
        currency: "SEK",
        hasPaid: false,
        amountPaid: 0,
      };
    })
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "sv"));
}

async function fetchClubMembersAsFinanceRows(clubID = null) {
  try {
    const res = await apiClient.get(`/api/members`, {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return mapClubUsersToFinanceRows(res.data);
  } catch {
    return [];
  }
}

// ---------- Finance API exports (read paths use financeGetJson; writes unchanged) ----------
export const fetchFinanceMembers = async (periodId = null, clubID = null) => {
  const params = mergeClubQueryParams(
    periodId != null && periodId !== "" ? { periodId } : {},
    clubID
  );
  const data = await financeGetJson(
    `/api/finance/members`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  const { normalizeFinanceMemberFromApi } = await import(
    "../../comp/admin/finance/memberBillingUtils"
  );
  const live = Array.isArray(data) ? data : [];
  if (live.length > 0) return filterSelectableMembers(live.map(normalizeFinanceMemberFromApi));

  if (isFinanceHttpSkipped()) {
    const roster = await fetchClubMembersAsFinanceRows(clubID);
    if (roster.length > 0) return filterSelectableMembers(roster);
  }

  return [];
};

export const fetchFinanceBillingPeriods = async (clubID = null) => {
  if (isFinanceServerDisabledByEnv()) return [];
  try {
    const res = await apiClient.get(`/api/finance/billing-periods`, {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    const data = res.data;
    if (!Array.isArray(data)) return [];
    const periods = data.map(normalizeBillingPeriodFromApi).filter(Boolean);
    if (periods.length > 0) clearFinanceHttpSkipped();
    return periods;
  } catch (err) {
    if (isFinanceRouteMissingError(err)) markFinanceHttpSkipped();
    throw new Error(financeApiErrorMessage(err, "Could not load billing periods"));
  }
};

export const createFinanceBillingPeriod = async (form, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    const res = await apiClient.post(
      `/api/finance/billing-periods`,
      billingPeriodFormToApiPayload(form),
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeBillingPeriodFromApi(res.data);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not create billing period"));
  }
};

export const patchFinanceBillingPeriod = async (id, form, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    const res = await apiClient.patch(
      `/api/finance/billing-periods/${encodeURIComponent(id)}`,
      billingPeriodFormToApiPayload(form),
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeBillingPeriodFromApi(res.data);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update billing period"));
  }
};

export const deleteFinanceBillingPeriod = async (id, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    await apiClient.delete(
      `/api/finance/billing-periods/${encodeURIComponent(id)}`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not delete billing period"));
  }
};

export const fetchFinanceCostTypes = async (clubID = null) => {
  if (isFinanceServerDisabledByEnv()) return [];
  try {
    const res = await apiClient.get(`/api/finance/cost-types`, {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not load cost types"));
  }
};

export const fetchFinanceAccounts = async (clubID = null) => {
  if (isFinanceServerDisabledByEnv()) return [];
  try {
    const res = await apiClient.get(`/api/finance/accounts`, {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not load accounts"));
  }
};

export const fetchFinanceAccountCostTypes = async (accountId, clubID = null) => {
  if (isFinanceServerDisabledByEnv()) return [];
  try {
    const res = await apiClient.get(
      `/api/finance/accounts/${encodeURIComponent(accountId)}/cost-types`,
      {
        headers: { ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not load account cost types"));
  }
};

export const createFinanceAccount = async (payload, clubID = null) => {
  if (isFinanceHttpSkipped()) throw new Error("Finance API is disabled");
  try {
    const res = await apiClient.post(`/api/finance/accounts`, payload, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not create account"));
  }
};

export const patchFinanceAccount = async (id, payload, clubID = null) => {
  if (isFinanceHttpSkipped()) throw new Error("Finance API is disabled");
  try {
    const res = await apiClient.patch(`/api/finance/accounts/${encodeURIComponent(id)}`, payload, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update account"));
  }
};

export const fetchFinanceGroupTypes = async (clubID = null) => {
  if (isFinanceHttpSkipped()) return [];
  const data = await financeGetJson(
    `/api/finance/group-types`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) },
    []
  );
  if (!Array.isArray(data)) return [];
  return data.map(normalizeClubGroupTypeFromApi).filter(Boolean);
};

export const createFinanceGroupType = async (form, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    const res = await apiClient.post(
      `/api/finance/group-types`,
      clubGroupTypeFormToApiPayload(form),
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeClubGroupTypeFromApi(res.data);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not create group type"));
  }
};

export const patchFinanceGroupType = async (id, form, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    const res = await apiClient.patch(
      `/api/finance/group-types/${encodeURIComponent(id)}`,
      clubGroupTypeFormToApiPayload(form),
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeClubGroupTypeFromApi(res.data);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update group type"));
  }
};

export const deleteFinanceGroupType = async (id, clubID = null) => {
  if (isFinanceHttpSkipped()) {
    throw new Error("Finance API is disabled");
  }
  try {
    await apiClient.delete(
      `/api/finance/group-types/${encodeURIComponent(id)}`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not delete group type"));
  }
};

export const fetchFinanceGroupCosts = async (periodId, filters = {}, clubID = null) => {
  if (!periodId) return [];
  if (isFinanceHttpSkipped()) return [];
  try {
    const params = mergeClubQueryParams({ ...filters, periodId }, clubID);
    const res = await apiClient.get(`/api/finance/group-costs`, {
      headers: { ...getAuthHeaders() },
      params,
    });
    const data = res.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeGroupCostRowFromApi(row, periodId));
  } catch (err) {
    // Period-not-found 404 must not flip global finance-off (unlike missing /api/finance routes).
    throw new Error(financeApiErrorMessage(err, "Could not load group costs"));
  }
};

function normalizeGroupCostRowFromApi(row, billingPeriodId) {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    id: row.id,
    billingPeriodId: row.billingPeriodId ?? billingPeriodId ?? row.periodId ?? null,
    groupId: String(row.groupId ?? row.groupID ?? ""),
    costTypeId: row.costTypeId ?? row.cost_type_id ?? "",
    invoiceLineText: row.invoiceLineText ?? row.invoice_line_text ?? "",
    amountSek: row.amountSek ?? row.amount_sek ?? 0,
    amountSekMonthly:
      row.amountSekMonthly ??
      row.amount_sek_monthly ??
      null,
    isRemoved: Boolean(row.isRemoved ?? row.is_removed),
  };
}

export const createFinanceGroupCost = async (payload, clubID = null) => {
  const billingPeriodId = payload?.billingPeriodId;
  if (!billingPeriodId) {
    throw new Error("Select a billing period saved on the server");
  }
  try {
    const res = await apiClient.post(
      `/api/finance/group-costs`,
      payload,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeGroupCostRowFromApi(res.data, billingPeriodId);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not create group cost"));
  }
};

export const patchFinanceGroupCost = async (id, patch, clubID = null) => {
  try {
    const res = await apiClient.patch(
      `/api/finance/group-costs/${encodeURIComponent(id)}`,
      patch,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return normalizeGroupCostRowFromApi(res.data);
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update group cost"));
  }
};

export const massUpdateFinanceGroupCosts = async (body, clubID = null) => {
  try {
    const res = await apiClient.post(
      `/api/finance/group-costs/mass-update`,
      body,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not mass-update group costs"));
  }
};

export const createFinancePayment = async ({
  studentID,
  amount,
  period_start,
  period_end,
  groupID,
  notes,
  billingPeriodId,
  clubID = null,
}) => {
  const body = { studentID, amount, period_start, period_end, groupID, notes };
  if (billingPeriodId) body.billingPeriodId = billingPeriodId;
  const res = await apiClient.post(
    `/api/finance/payments`,
    body,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const sendFinanceReminder = async (studentID, periodId = null, clubID = null) => {
  const body = { studentID };
  if (periodId != null && periodId !== "") body.periodId = periodId;
  const res = await apiClient.post(
    `/api/finance/send-reminder`,
    body,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const patchFinanceMemberBilling = async (periodId, studentID, patch, clubID = null) => {
  const res = await apiClient.patch(
    `/api/finance/member-billing`,
    { periodId, studentID, ...patch },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const stopFinanceMemberNotice = async (studentID, periodId = null, clubID = null) => {
  const body = { studentID };
  if (periodId != null && periodId !== "") body.periodId = periodId;
  const res = await apiClient.post(
    `/api/finance/stop-member-notice`,
    body,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

/** Dev-only: clear payments, invoice links, and aviserad state for re-testing. */
export const resetFinanceMemberBillingTest = async (studentID, periodId = null, clubID = null) => {
  const body = { studentID };
  if (periodId != null && periodId !== "") body.periodId = periodId;
  const res = await apiClient.post(
    `/api/finance/reset-member-billing-test`,
    body,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const fetchFinanceMemberCostBreakdown = async (studentID, periodId = null, clubID = null) => {
  const params = mergeClubQueryParams(
    {
      studentID,
      ...(periodId != null && periodId !== "" ? { periodId } : {}),
    },
    clubID
  );
  const res = await apiClient.get(
    `/api/finance/member-cost-breakdown`,
    {
      headers: { ...getAuthHeaders() },
      params,
    }
  );
  return res.data;
};

export const createFinanceMemberCost = async (payload, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/member-costs`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const deleteFinanceMemberCost = async (id, clubID = null) => {
  await apiClient.delete(
    `/api/finance/member-costs/${encodeURIComponent(id)}`,
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
};

export const fetchFinanceGroupNotices = async (periodId, clubID = null) => {
  if (!periodId) return [];
  const params = mergeClubQueryParams({ periodId }, clubID);
  const data = await financeGetJson(
    `/api/finance/group-notices`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  return Array.isArray(data) ? data : [];
};

export const patchFinanceGroupNotice = async (periodId, groupId, patch, clubID = null) => {
  const res = await apiClient.patch(
    `/api/finance/group-notices/${encodeURIComponent(groupId)}`,
    { periodId, ...patch },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const massUpdateFinanceGroupNotices = async (periodId, patches, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/group-notices/mass-update`,
    { periodId, patches },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const sendFinanceGroupNotice = async (periodId, groupId, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/group-notices/${encodeURIComponent(groupId)}/send`,
    { periodId },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const fetchFinanceGroupNoticeSendRuns = async (periodId, groupId, clubID = null, limit = 5) => {
  const params = mergeClubQueryParams({ periodId, limit }, clubID);
  const data = await financeGetJson(
    `/api/finance/group-notices/${encodeURIComponent(groupId)}/send-runs`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  return Array.isArray(data) ? data : [];
};

export const fetchFinancePaymentHistory = async (studentID, periodId = null, clubID = null) => {
  const params = mergeClubQueryParams(
    {
      studentID,
      ...(periodId != null && periodId !== "" ? { periodId } : {}),
    },
    clubID
  );
  const data = await financeGetJson(
    `/api/finance/payments`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  const { normalizeFinancePaymentHistoryRow } = await import(
    "../../comp/admin/finance/memberBillingUtils"
  );
  const rows = Array.isArray(data) ? data : [];
  return rows.map(normalizeFinancePaymentHistoryRow).filter(Boolean);
};

/** Own payment history only — uses JWT identity on the server (/api/me/payments). */
export const fetchMyPaymentHistory = async (clubID = null) => {
  const params = mergeClubQueryParams({}, clubID);
  const data = await financeGetJson(
    `/api/me/payments`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  const { normalizeFinancePaymentHistoryRow } = await import(
    "../../comp/admin/finance/memberBillingUtils"
  );
  const rows = Array.isArray(data) ? data : [];
  return rows.map(normalizeFinancePaymentHistoryRow).filter(Boolean);
};

/** Cancel the member's monthly Stripe subscription at period end. */
export const cancelMySubscription = async (clubID = null) => {
  const res = await apiClient.post(
    `/api/me/subscription/cancel`,
    {},
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

/** Test: force access expiry now so the payment-required screen appears. */
export const simulateMyMembershipExpiry = async (clubID = null) => {
  const res = await apiClient.post(
    `/api/me/subscription/simulate-expiry`,
    {},
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const sendFinancePaymentLink = async (studentID, periodId = null, clubID = null) => {
  const body = { studentID };
  if (periodId != null && periodId !== "") body.periodId = periodId;
  try {
    const res = await apiClient.post(
      `/api/finance/send-payment-link`,
      body,
      {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    const msg = financeApiErrorMessage(err, "Could not send payment link");
    const wrapped = new Error(msg);
    wrapped.response = err.response;
    throw wrapped;
  }
};

/** Create Stripe Checkout Session for finance payment. Returns { url }. Redirect user to url for payment. */
export const createFinanceCheckoutSession = async (payload = {}) => {
  const { studentID, amount, currency, periodId, clubID = null } = payload;
  const res = await apiClient.post(
    `/api/finance/create-checkout-session`,
    { studentID, amount, currency, periodId },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

/** Public invoice page (no auth). */
export const fetchPublicInvoice = async (token) => {
  const res = await apiClient.get(
    `/api/finance/invoice/${encodeURIComponent(token)}`
  );
  return res.data;
};

/** Public invoice terms (no auth). */
export const fetchPublicInvoiceTerms = async (token) => {
  const res = await apiClient.get(
    `/api/finance/invoice/${encodeURIComponent(token)}/terms`
  );
  return res.data;
};

export const verifyInvoiceCheckout = async (token, sessionId) => {
  const res = await apiClient.get(
    `/api/finance/invoice/${encodeURIComponent(token)}/verify-checkout`,
    { params: { session_id: sessionId } }
  );
  return res.data;
};

/** Start Stripe checkout from public invoice page. */
export const createInvoiceCheckout = async (
  token,
  paymentMethod = "stripe",
  { termsAccepted = false, locale = null } = {}
) => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/checkout`,
    {
      paymentMethod,
      termsAccepted: termsAccepted === true,
      ...(locale ? { locale } : {}),
    },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const selectInvoiceMembershipPlan = async (token, plan) => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/select-plan`,
    { plan },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const clearInvoicePlanSelection = async (token) => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/clear-plan`,
    {},
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const confirmInvoiceBreakdown = async (token) => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/confirm-breakdown`,
    {},
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const fetchFinanceInactiveMemberships = async (clubID = null) => {
  const res = await apiClient.get(
    `/api/finance/membership-inactive`,
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return Array.isArray(res.data) ? res.data : [];
};

export const sendMembershipConversionInvoice = async (studentID, clubID = null, sendEmail = true) => {
  const res = await apiClient.post(
    `/api/finance/membership/${encodeURIComponent(studentID)}/conversion-invoice`,
    { sendEmail },
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const markMembershipLapsed = async (studentID, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/membership/${encodeURIComponent(studentID)}/mark-lapsed`,
    {},
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const dismissInactiveMembership = async (studentID, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/membership/${encodeURIComponent(studentID)}/dismiss-inactive`,
    {},
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const submitInvoiceOptOut = async (token, groupId, comment = "") => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/opt-out`,
    { groupId, comment },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

/** Decline membership payment and leave the club (archives account). */
export const submitMembershipInvoiceOptOut = async (token) => {
  const res = await apiClient.post(
    `/api/finance/invoice/${encodeURIComponent(token)}/membership-opt-out`,
    {},
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

export const fetchFinanceGroupNoticeOptOuts = async (periodId, groupId = null, clubID = null) => {
  const params = mergeClubQueryParams(
    {
      periodId,
      ...(groupId != null && groupId !== "" ? { groupId } : {}),
    },
    clubID
  );
  const data = await financeGetJson(
    `/api/finance/group-notices/opt-outs`,
    { headers: { ...getAuthHeaders() }, params },
    []
  );
  return Array.isArray(data) ? data : [];
};

export const restoreFinanceGroupNoticeOptOut = async (optOutId, clubID = null) => {
  const res = await apiClient.post(
    `/api/finance/group-notices/opt-outs/${encodeURIComponent(optOutId)}/restore`,
    {},
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const deleteFinanceGroupNoticeOptOut = async (optOutId, clubID = null) => {
  const res = await apiClient.delete(
    `/api/finance/group-notices/opt-outs/${encodeURIComponent(optOutId)}`,
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const fetchFinancePaymentSettings = async (clubID = null) => {
  const data = await financeGetJson(
    `/api/finance/payment-settings`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) },
    {
      invoiceLinkValidityDays: 14,
      sieReceivablesAccount: "",
      sieVerificationSeries: "",
      ageDisplay: "full_ssn",
      showGuardianAddressNote: false,
      showTrainingTimes: false,
      termsGeneral: "",
      termsBookings: "",
      termsTeamFund: "",
    }
  );
  return data;
};

export const updateFinancePaymentSettings = async (payload, clubID = null) => {
  try {
    const res = await apiClient.patch(
      `/api/finance/payment-settings`,
      payload,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update payment settings"));
  }
};

// ---------- POS (platform Stripe; club Connect later) ----------

export const fetchPosStorefront = async (clubID = null) => {
  try {
    const res = await apiClient.get(
      `/api/pos/storefront`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return res.data;
  } catch {
    return { shopEnabled: false, visibleInMenu: false, currency: "SEK" };
  }
};

export const fetchPosSettings = async (clubID = null) => {
  const res = await apiClient.get(
    `/api/pos/settings`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return res.data;
};

export const updatePosSettings = async (payload, clubID = null) => {
  try {
    const res = await apiClient.patch(
      `/api/pos/settings`,
      payload,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update POS settings"));
  }
};

export const fetchPosProducts = async ({ admin = true, status, clubID = null } = {}) => {
  const params = mergeClubQueryParams({}, clubID);
  if (admin) params.admin = "true";
  if (status) params.status = status;
  const res = await apiClient.get(
    `/api/pos/products`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return res.data;
};

export const createPosProduct = async (payload, clubID = null) => {
  try {
    const res = await apiClient.post(
      `/api/pos/products`,
      payload,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not create product"));
  }
};

export const updatePosProduct = async (id, payload, clubID = null) => {
  try {
    const res = await apiClient.patch(
      `/api/pos/products/${encodeURIComponent(id)}`,
      payload,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not update product"));
  }
};

export const deletePosProduct = async (id, clubID = null) => {
  try {
    const res = await apiClient.delete(
      `/api/pos/products/${encodeURIComponent(id)}`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not delete product"));
  }
};

export const uploadPosProductImage = async (file, clubID = null) => {
  try {
    const formData = new FormData();
    formData.append("image", file);
    const res = await apiClient.post(
      `/api/pos/products/upload-image`,
      formData,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not upload product image"));
  }
};

export const createPosCheckout = async (items, clubID = null, { customerStudentID } = {}) => {
  try {
    const body = { items };
    if (customerStudentID != null && customerStudentID !== "") {
      body.customerStudentID = customerStudentID;
    }
    const res = await apiClient.post(
      `/api/pos/checkout`,
      body,
      {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not start checkout"));
  }
};

export const verifyPosCheckout = async (sessionId, clubID = null) => {
  try {
    const res = await apiClient.get(
      `/api/pos/checkout/verify`,
      {
        params: mergeClubQueryParams({ session_id: sessionId }, clubID),
        headers: { ...getAuthHeaders() },
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not verify payment"));
  }
};

/** Public receipt load after Stripe redirect (no auth required). */
export const fetchPosReceipt = async (sessionId) => {
  try {
    const res = await apiClient.get(
      `/api/pos/checkout/receipt`,
      { params: { session_id: sessionId } }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not load receipt"));
  }
};

/** Mark pending order cancelled when buyer returns from Stripe. */
export const cancelPosCheckout = async (sessionId) => {
  try {
    const res = await apiClient.post(
      `/api/pos/checkout/cancel`,
      { session_id: sessionId }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not cancel checkout"));
  }
};

export const markPosOrderDelivered = async (orderId, clubID = null) => {
  try {
    const res = await apiClient.patch(
      `/api/pos/orders/${orderId}/deliver`,
      {},
      {
        headers: { ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not mark order as delivered"));
  }
};

export const deletePosOrder = async (orderId, clubID = null) => {
  try {
    const res = await apiClient.delete(
      `/api/pos/orders/${orderId}`,
      {
        headers: { ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not remove order"));
  }
};

export const fetchPosOrders = async ({ studentID, clubID = null } = {}) => {
  const params = mergeClubQueryParams(
    studentID != null ? { studentID: String(studentID) } : {},
    clubID
  );
  const res = await apiClient.get(
    `/api/pos/orders`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return res.data;
};

export const fetchPosStats = async ({ period = "30d", from, to, clubID = null } = {}) => {
  try {
    const params = mergeClubQueryParams({ period }, clubID);
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await apiClient.get(
      `/api/pos/stats`,
      { headers: { ...getAuthHeaders() }, params }
    );
    return res.data;
  } catch (err) {
    throw new Error(financeApiErrorMessage(err, "Could not load POS statistics"));
  }
};

export const fetchMembersByClubID = async () => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  // Note: Function name kept for backward compatibility, but no longer accepts clubID parameter
  const response = await apiClient.get(
    `/api/members`,
    { 
      headers: { ...getAuthHeaders() } 
    }
  );
  return filterSelectableMembers(response.data);
};

export const fetchMembersByClubIDUserAdmin = async () => {
  const response = await apiClient.get(
    `/api/useradmin/members`,
    { headers: { ...getAuthHeaders() } }
  );
  return filterSelectableMembers(response.data);
};

export const fetchArchivedMembersUserAdmin = async () => {
  const response = await apiClient.get(
    `/api/useradmin/archived-members`,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

/** @deprecated Prefer deleteMyAccount — archive-my-account now performs permanent deletion. */
export const archiveMyAccount = async (studentID, confirmEmail, clubID = null) => {
  return deleteMyAccount(studentID, { consent: true, confirmEmail, clubID });
};

export const deleteMyAccount = async (studentID, { consent = false, confirmEmail, clubID = null } = {}) => {
  const response = await apiClient.post(
    `/api/delete-my-account`,
    { studentID, consent: consent === true, confirmEmail: confirmEmail || undefined },
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const archiveMemberByAdmin = async (studentID, clubID = null) => {
  const formData = new FormData();
  formData.append("studentID", String(studentID));
  const response = await apiClient.post(
    `/api/useradmin/archive-member`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const restoreArchivedMember = async (studentID, clubID = null) => {
  const formData = new FormData();
  formData.append("studentID", String(studentID));
  const response = await apiClient.post(
    `/api/useradmin/restore-member`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

/** GDPR Art. 17 — anonymize PII for an archived member (admin only). */
export const gdprAnonymizeArchivedMember = async (studentID, clubID = null, referenceId = "") => {
  const formData = new FormData();
  formData.append("studentID", String(studentID));
  if (referenceId) formData.append("referenceId", String(referenceId));
  const response = await apiClient.post(
    `/api/useradmin/gdpr-anonymize-member`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const createNewUserByAdmin = async (userData, imageFile) => {
  if (!userData.email) {
    throw new Error('Email is required');
  }

  // 1. Upload image to Supabase Storage if provided (we need studentID - will do after create)
  let imageUrl = userData.imageUrl;

  // 2. Create user in Auth + DB (backend generates studentID and temp password)
  const payload = {
    name: userData.name,
    email: userData.email,
    personnummer: userData.personnummer?.trim() || null,
    address: userData.address || null,
    parentID: userData.parentID || null,
    parentID2: userData.parentID2 || null,
    isParent: userData.isParent === true || userData.isParent === 'true',
    isStudent: userData.isStudent !== false && userData.isStudent !== 'false',
    clubID: userData.clubID,
    clubname: userData.clubname,
  };
  if (userData.grantAccessWithoutPayment === true || userData.grantAccessWithoutPayment === 'true') {
    payload.grantAccessWithoutPayment = true;
  }
  if (imageUrl) payload.imageUrl = imageUrl;

  const createRes = await apiClient.post(
    `/api/create-user-by-admin`,
    payload,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );

  const newUser = createRes.data?.user;
  if (!newUser) return createRes.data;

  // 3. If image file provided, upload to storage and update user
  if (imageFile && imageFile instanceof File && newUser.studentID) {
    try {
      const { uploadUserProfile } = await import('../utils/supabaseStorage');
      const { compressImage } = await import('../utils/imageUtils');
      const fileToDataUrl = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      const compressedImage = await compressImage(imageFile);
      const base64Image = await fileToDataUrl(new File([compressedImage], imageFile.name, { type: imageFile.type }));
      const base64String = base64Image.split(',')[1];
      const uploadResult = await uploadUserProfile(base64String, newUser.studentID, imageFile.type);
      if (uploadResult?.success && uploadResult?.data?.publicUrl) {
        const formData = new FormData();
        formData.append('studentID', newUser.studentID);
        formData.append('originalStudentID', newUser.studentID);
        formData.append('imageUrl', uploadResult.data.publicUrl);
        await apiClient.post(
          `/api/updateUserByAdmin`,
          formData,
          { headers: { ...getAuthHeaders() }, params: newUser.clubID ? { clubID: newUser.clubID } : {} }
        );
      }
    } catch (imgErr) {
      console.warn('User created but image upload failed:', imgErr);
    }
  }

  return createRes.data;
};

/**
 * Admin: set a new temporary password and email it.
 * Uses axios (not apiClient) so a 401 from the connection API does not run apiClient's global logout/redirect.
 */
export const adminSendTemporaryPassword = async ({ email, name, studentID }) => {
  const debug =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_DEBUG_USER_ADMIN_TEMP_PASSWORD === "true";

  const emailTrim = String(email || "").trim();
  const sid = studentID != null && studentID !== "" ? String(studentID).trim() : "";
  if (!sid && !emailTrim) {
    throw new Error("studentID or email is required");
  }
  const body = {
    ...(emailTrim ? { email: emailTrim } : {}),
    ...(sid ? { studentID: sid } : {}),
    ...(name != null && String(name).trim() ? { name: String(name).trim() } : {}),
  };
  const url = `${process.env.REACT_APP_CONNECTION_API}/api/useradmin/send-temporary-password`;
  if (debug) {
    console.info("[api] adminSendTemporaryPassword POST", { url, body });
  }
  try {
    const response = await axios.post(url, body, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    });
    if (debug) {
      console.info("[api] adminSendTemporaryPassword OK", response.status, response.data);
    }
    return response.data;
  } catch (err) {
    const data = err.response?.data;
    if (debug) {
      console.error("[api] adminSendTemporaryPassword FAILED", {
        status: err.response?.status,
        data,
        backendDebug: data?.debug || null,
        hint: data?.hint || null,
      });
    } else if (data?.debug) {
      console.error("[api] adminSendTemporaryPassword FAILED (set DEBUG_SEND_TEMP_PASSWORD=true on backend for full trace)", {
        status: err.response?.status,
        error: data?.error,
        debug: data.debug,
      });
    }
    throw err;
  }
};

export const releaseUserFromParents = async (studentID, newEmail) => {
  const response = await apiClient.post(
    `/api/release-user-from-parents`,
    { studentID, newEmail },
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return response.data;
};

export const updateUserByAdmin = async (userData, imageFile) => {

  try {
    // Handle image upload to Supabase Storage (frontend)
    if (imageFile && imageFile instanceof File) {
      
      // Import the upload function
      const { uploadUserProfile } = await import('../utils/supabaseStorage');
      const { compressImage } = await import('../utils/imageUtils');
      
      // Helper function to convert File to base64
      const fileToDataUrl = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      };
      
      // Compress the image first
      const compressedImage = await compressImage(imageFile);
      const compressedFile = new File([compressedImage], imageFile.name, { type: imageFile.type });
      
      // Convert to base64 for Supabase upload
      const base64Image = await fileToDataUrl(compressedFile);
      const base64String = base64Image.split(',')[1]; // Remove data:image/...;base64, prefix
      
      // Get studentID from userData
      const studentID = userData.originalStudentID || userData.studentID;
      
      // Upload to Supabase Storage
      const uploadResult = await uploadUserProfile(
        base64String,
        studentID,
        imageFile.type
      );
      
      if (uploadResult.success) {
        // Add the Supabase URL to userData instead of sending the file
        userData.imageUrl = uploadResult.data.publicUrl;
      } else {
        throw new Error('Failed to upload image to Supabase Storage');
      }
    }
    
    // Now send the data (without the file) to the backend
    const formData = new FormData();
    
    // Append all user data fields to FormData
    for (const key in userData) {
      if (userData[key] !== undefined && userData[key] !== null) {
        formData.append(key, userData[key]);
      }
    }
    
    // Pass clubID as query param for admin context (enforceClubID uses it)
    const params = (userData.clubID != null && userData.clubID !== '') ? { clubID: userData.clubID } : {};
    
    const res = await apiClient.post(
      `/api/updateUserByAdmin`,
      formData,
      {
        params,
        headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() }
      }
    );
    
    return res.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update studentID across all related tables in the database
 * This function should be called BEFORE updating the user record itself
 * @param {string|number} oldStudentID - The current/existing studentID
 * @param {string|number} newStudentID - The new studentID to update to
 * @param {string|number} [clubID] - Club ID for admin context (required when admin manages multiple clubs)
 * @returns {Promise} - API response
 */
export const updateStudentID = async (oldStudentID, newStudentID, clubID = null) => {
  try {
    const params = (clubID != null && clubID !== '') ? { clubID } : {};
    const res = await apiClient.post(
      `/api/updateStudentID`,
      {
        oldStudentID: String(oldStudentID),
        newStudentID: String(newStudentID),
      },
      {
        params,
        headers: { ...getAuthHeaders() }
      }
    );
    
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserSettingsPermissions = async (
  userId,
  settingsPermissions,
  { clubID = null, birthdayKioskLanguage = undefined } = {}
) => {
  const params = clubID != null ? { clubID } : {};
  const body = { userId, settingsPermissions };
  if (birthdayKioskLanguage !== undefined) {
    body.birthdayKioskLanguage = birthdayKioskLanguage;
  }
  const response = await apiClient.post(
    `/api/update-user-settings-permissions`,
    body,
    { params, headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

/**
 * Request password reset email via backend (generateLink + app SMTP).
 * Avoids Supabase /auth/v1/recover 504 when Supabase email is slow.
 */
export const requestPasswordResetEmail = async (email, redirectTo) => {
  const response = await apiClient.post(
    `/api/auth/request-password-reset`,
    { email: String(email).trim().toLowerCase(), redirectTo },
    {
      headers: { "Content-Type": "application/json" },
      timeout: 60000,
    }
  );
  return response.data;
};

/** Club 1: download a Badge Studio .bs file for the selected members. */
export const exportMemberCardsBs = async (studentIDs, clubID = null) => {
  const response = await apiClient.post(
    `/api/useradmin/export-member-cards`,
    { studentIDs },
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
      responseType: "blob",
    }
  );
  return response.data;
};

export const deleteUsersByAdmin = async (studentIDs, clubID = null) => {
  // Build the form data
  const formData = new FormData();
  formData.append("studentID", JSON.stringify(studentIDs));

  // Pass clubID for admin multi-club context; backend enforceClubID uses ?clubID=
  const params = clubID != null ? { clubID } : {};

  const res = await apiClient.post(
    `/api/deleteUsersByAdmin`,
    formData,
    {
      params,
      headers: {
        "Content-Type": "multipart/form-data",
        ...getAuthHeaders(),
      },
    }
  );

  return res.data;
};

export const fetchFamilyMembersByParent = async (parentID, groupData) => {
   try {
     const response = await apiClient.get(
       `/api/users-by-parent?parentID=${parentID}`,
       { headers: { ...getAuthHeaders() } }
     );
     
     // Handle null or undefined response.data
     if (!response.data || !Array.isArray(response.data)) {
       return [];
     }
     
     return response.data.map((member) => {
       const group = groupData.find((g) => g.groupID === member.groupID);
       return { ...member, groupName: group ? group.groupName : "No group" };
     });
   } catch (error) {
     return [];
   }
};

export const removeFamilyMember = async (studentID) => {
  await apiClient.post(
    `/api/remove-family-member`,
    { studentID },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
};

export const removeSecondParent = async (studentID) => {
  await apiClient.post(
    `/api/remove-family-member-second-parent`,
    { studentID },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
};

export const fetchPendingParentRequests = async () => {
  const response = await apiClient.get(
    `/api/parent-requests/pending`,
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const approveParentRequest = async (studentID, groupID = null) => {
  const body = { studentID };
  if (groupID != null && groupID !== "") {
    body.groupID = groupID;
  }
  const response = await apiClient.post(`/api/parent-requests/approve`, body, {
    headers: { ...getAuthHeaders() },
  });
  return response.data;
};

export const rejectParentRequest = async (studentID, email, reason) => {
  await apiClient.post(
    `/api/parent-requests/reject`,
    { studentID, email, reason },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
}

export const fetchChildPromotionsByStudentID = async (studentID, options = {}) => {
  const params = {};
  if (options.year != null) params.year = options.year;
  const response = await apiClient.get(
    `/api/child-student-promotions/${studentID}`,
    { params, headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const fetchPendingChildPromotions = async () => {
   const response = await apiClient.get(
     `/api/child-promotions`,
     { headers: { ...getAuthHeaders() } }
   );
   return response.data.filter((promotion) => !promotion.promotionApproved);

};

export const sendColorPromotionReminders = async (studentIDs, customMessage = '') => {
  const response = await apiClient.post(
    `/api/color-promotion-reminder-send`,
    { studentIDs, customMessage: customMessage || '' },
    { headers: { ...getAuthHeaders() } }
  );
  return response.data;
};

export const submitColorPromotionRequest = async (payload) => {
  try {
    const response = await apiClient.post(
      `/api/color-promotion-request`,
      payload,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting color promotion request:', error);
    throw error;
  }
};

export const submitColorPromotionUpdate = async (payload) => {
  try {
    const response = await apiClient.post(
      `/api/update-color-promotion`,
      payload,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating color promotion:', error);
    throw error;
  }
};

export const submitPromotionRequest = async ({ studentID, belt, stripe, promotor, promotionDate }) => {
   await apiClient.post(
     `/api/promotion-request`,
     { studentID, belt, stripe, promotor, promotionDate },
     { headers: { ...getAuthHeaders() } }
   );
};

export const approveChildPromotions = async (promotions, messageContent, adminID) => {
  try {
    const response = await apiClient.post(
      `/api/approve-child-promotions`,
      { promotions, messageContent, adminID },
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendPromotionMessage = async (
  studentID, recipientName, messageContent, adminID, threadId = null
) => {
   const payload = {
     studentID, adminID, messageContent,
     ...(threadId && { threadId }),
   };
   await apiClient.post(
     `/api/send-promotion-message`,
    payload,
     { headers: { ...getAuthHeaders() } }
  );
  return;
};

export const sendMentorMessage = async (
  recipients,            // either a single ID or an array of IDs
  messageContent,
  adminID,
  threadId = null
) => {
  // // Build base payload
  // const payload = {
  //   adminID,
  //   messageContent,
  //   ...(threadId && { threadId }),
  // };

  // // If they passed multiple, send as recipients array,
  // // otherwise send single studentID
  // if (Array.isArray(recipients)) {
  //   payload.recipients = recipients.map(id => ({ studentID: id }));
  // } else {
  //   payload.studentID = recipients;
  // }

  // await apiClient.post(
  //   `/api/send-mentor-message`,
  //   payload,
  //   { headers: getAuthHeaders() }
  // );
  return; // Temporary return for migration
};

export const patchClubGroup = async (groupId, body) => {
  await apiClient.put(
    `/api/groups/${encodeURIComponent(groupId)}`,
    body,
    { headers: { "Content-Type": "application/json", ...getAuthHeaders() } }
  );
};

export const updateGroupName = async (groupId, groupName) => {
  await patchClubGroup(groupId, { groupName });
};

export const deleteGroup = async (groupId) => {
  await apiClient.delete(
    `/api/groups/${groupId}`,
    { headers: { ...getAuthHeaders() } }
  );
};

export const createGroup = async (
  groupId,
  groupName,
  kidsGroup,
  colorPromotion,
  instructorsGroup,
  ungdom,
  vuxen,
  groupTypeId = null,
  detailFields = null
) => {
  const u = !!ungdom;
  const v = !!vuxen;
  const body = {
    groupID: groupId,
    groupName,
    kidsGroup,
    colorPromotion,
    instructorsGroup,
    ungdom: u,
    vuxen: v,
    isUngdom: u,
    isVuxen: v,
  };
  if (groupTypeId) body.groupTypeId = groupTypeId;
  if (detailFields && typeof detailFields === "object") {
    Object.assign(body, detailFields);
  }
  await apiClient.post(`/api/groups`, body, {
    headers: { ...getAuthHeaders() },
  });
};

export const addMembersToGroup = async (
  studentIds, newGroupID, kidsGroup, colorPromotion, instructorsGroup, ungdom, vuxen
) => {
  const { data } = await apiClient.put(
    `/api/users/group`,
    { studentIds, newGroupID, kidsGroup, colorPromotion, instructorsGroup, ungdom: !!ungdom, vuxen: !!vuxen },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
  return data;
};

export const updateUserGroup = async (
  studentIds, newGroupID, kidsGroup, colorPromotion, instructorsGroup, ungdom, vuxen
) => {
  await apiClient.put(
    `/api/users/group`,
    { studentIds, newGroupID, kidsGroup, colorPromotion, instructorsGroup, ungdom: !!ungdom, vuxen: !!vuxen },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
};

export const removeUserGroup = async (studentIds, groupID) => {
  await apiClient.put(
    `/api/users/group/remove`,
    { studentIds, groupID },
    { headers: { ...getAuthHeaders() } }
  );
  clearLocalStorage();
};

// export const postAttendance = async ({ studentID, today, classId }) => {
//   const response = await apiClient.post(
//     `/api/attendance`,
//     { studentID, today, classId },
//     { headers: { ...getAuthHeaders() } }
//   );
//   return response.data;
// };

export const fetchClasses = async (groupIDs = [], clubID = null) => {
  const params = { ...(groupIDs.length > 0 && { groupIDs: groupIDs.join(',') }), ...(clubID != null && { clubID }) };
  const res = await apiClient.get(
     `/api/classes`,
     { 
       params,
      headers: { ...getAuthHeaders() } 
    }
   );
   return res.data;
};

export const fetchInstructorClasses = async (instructorStudentID = null) => {
  const params = {};
  if (instructorStudentID != null && instructorStudentID !== '') {
    params.instructorStudentID = instructorStudentID;
  }
  const res = await apiClient.get(
    `/api/instructor-classes`,
    { params, headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

/** Instructor availability (who said Yes/No per class). Admin: any date/range; instructor: today only (server-enforced). */
export const fetchInstructorAvailability = async (fromDate, toDate, clubID = null) => {
  const params = mergeClubQueryParams({}, clubID);
  if (fromDate) params.from = fromDate;
  if (toDate) params.to = toDate;
  if (!fromDate && !toDate) params.date = new Date().toISOString().split('T')[0];
  const res = await apiClient.get(
    `/api/instructor-availability`,
    { params, headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

export const fetchInstructorAvailabilityEmailSettings = async () => {
  const res = await apiClient.get(
    `/api/instructor-availability/email-settings`,
    { headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

export const patchInstructorAvailabilityEmailSettings = async (patch) => {
  const res = await apiClient.patch(
    `/api/instructor-availability/email-settings`,
    patch,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
};

/** Träningsgrupper → Kallelser/Utskick (queued, Resend-throttled on server). */
export const sendGroupMailing = async (formData, options = {}) => {
  const res = await apiClient.post(
    `/api/group-mailings/send`,
    formData,
    {
      headers: { ...getAuthHeaders() },
      timeout: 120000,
      signal: options.signal,
    }
  );
  return res.data;
};

export const fetchGroupMailingJobStatus = async (jobId) => {
  const res = await apiClient.get(
    `/api/group-mailings/jobs/${encodeURIComponent(jobId)}`,
    { headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

export const cancelGroupMailingJob = async (jobId) => {
  const res = await apiClient.post(
    `/api/group-mailings/jobs/${encodeURIComponent(jobId)}/cancel`,
    {},
    { headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

export const fetchGroupMailingJobs = async ({ limit = 50, offset = 0 } = {}) => {
  const res = await apiClient.get(`/api/group-mailings/jobs`, {
    headers: { ...getAuthHeaders() },
    params: { limit, offset },
  });
  return res.data;
};

export const fetchGroupMailingJobDetail = async (jobId) => {
  const res = await apiClient.get(
    `/api/group-mailings/jobs/${encodeURIComponent(jobId)}/detail`,
    { headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

export const deleteGroupMailingJob = async (jobId) => {
  const res = await apiClient.delete(
    `/api/group-mailings/jobs/${encodeURIComponent(jobId)}`,
    { headers: { ...getAuthHeaders() } }
  );
  return res.data;
};

/** Manual send: trigger availability emails for a class (admin only). */
export const sendInstructorAvailabilityEmails = async (classId, date) => {
  const res = await apiClient.post(
    `/api/instructor-availability/send`,
    { classId, date },
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
};

/** Manual send: all classes for this club on target date (same as daily cron, this club only). */
export const sendInstructorAvailabilityScheduledEmails = async ({ date, resend } = {}) => {
  const res = await apiClient.post(
    `/api/instructor-availability/send-scheduled`,
    { date, resend: Boolean(resend) },
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
};

/** Instructor: update or add own (or selected family member) availability response (yes/no). */
export const updateInstructorAvailabilityResponse = async (classId, date, response, studentID = null, clubID = null) => {
  const body = { classId, date, response };
  if (studentID != null && studentID !== '') {
    body.studentID = studentID;
  }
  const res = await apiClient.put(
    `/api/instructor-availability/respond`,
    body,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export async function fetchActiveBillingPeriod() {
  const res = await apiClient.get(`/api/club/active-billing-period`, {
    headers: { ...getAuthHeaders() },
  });
  return res.data;
}

/** Billing periods for members (attendance profile) — excludes hidden periods. */
export async function fetchClubBillingPeriods() {
  const res = await apiClient.get(`/api/club/billing-periods`, {
    headers: { ...getAuthHeaders() },
  });
  const data = res.data;
  if (!Array.isArray(data)) return [];
  return data.map(normalizeBillingPeriodFromApi).filter(Boolean);
}

export async function addAttendance(record) {
  const res = await apiClient.post(
    `/api/attendance`,
    record,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
}

export async function recordVisits(visits) {
  return Promise.all(visits.map(v => addAttendance(v)));
}

export async function recordVisitsWithPartialSuccess(visits) {
  // const results = await Promise.allSettled(visits.map(v => addAttendance(v)));
  // const successful = [];
  // const failed = [];
  // 
  // results.forEach((result, index) => {
  //   if (result.status === 'fulfilled') {
  //     successful.push(visits[index]);
  //   } else {
  //     failed.push({ visit: visits[index], error: result.reason });
  //   }
  // });
  // 
  // return { successful, failed };
  return { successful: visits, failed: [] }; // Temporary return for migration
}

export async function addInstructorAttendance(record) {
  const res = await apiClient.post(
    `/api/instructor-attendance`,
    record,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
}

export async function addInstructorAttendancePost(record) {
  const res = await apiClient.post(
    `/api/instructor-attendance-post`,
    record,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return res.data;
}

export async function recordInstructorVisits(visits) {
  return Promise.all(visits.map(v => addInstructorAttendance(v)));
}

export async function recordInstructorVisitsPost(visits) {
  return Promise.all(visits.map(v => addInstructorAttendancePost(v)));
}

export async function recordInstructorVisitsWithPartialSuccess(visits) {
  // const results = await Promise.allSettled(visits.map(v => addInstructorAttendance(v)));
  // const successful = [];
  // const failed = [];
  // 
  // results.forEach((result, index) => {
  //   if (result.status === 'fulfilled') {
  //     successful.push(visits[index]);
  //   } else {
  //     failed.push({ visit: visits[index], error: result.reason });
  //   }
  // });
  // 
  // return { successful, failed };
  return { successful: visits, failed: [] }; // Temporary return for migration
}

export async function fetchInstructorAttendance(studentID) {
  // const res = await apiClient.get(
  //   `/api/instructor-attendance/${studentID}`,
  //   {
  //     headers: {
  //       'Content-Type': 'application/json',
  //       ...getAuthHeaders(),
  //     },
  //   }
  // );
  // return res.data;
  return []; // Temporary return for migration
}

// Fetch user's regular attendance for today
export async function fetchUserRegularAttendanceToday(studentID) {
  const res = await apiClient.get(
    `/api/attendance/${studentID}/today`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    }
  );
  return res.data || [];
}

// Fetch user's instructor attendance for today
export async function fetchUserInstructorAttendanceToday(studentID) {
  const res = await apiClient.get(
    `/api/instructor-attendance/${studentID}/today`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    }
  );
  return res.data || [];
}

// Fetch user's instructor attendance for a date range (registration window)
export async function fetchUserInstructorAttendanceRecent(studentID, fromDate, toDate) {
  const params = new URLSearchParams();
  if (fromDate) params.append('from', fromDate);
  if (toDate) params.append('to', toDate);
  const query = params.toString();
  const res = await apiClient.get(
    `/api/instructor-attendance/${studentID}/recent${query ? `?${query}` : ''}`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    }
  );
  return res.data || [];
}

// Try to fetch user's attendance for today - handles missing endpoints gracefully
export async function fetchUserAttendanceToday(studentID) {
  // const today = new Date().toISOString().split('T')[0];
  // 
  // try {
  //   // Try to get instructor attendance first
  //   const instructorAttendance = await fetchInstructorAttendance(studentID);
  //   
  //   // Filter for today's attendance
  //   const todayInstructorAttendance = Array.isArray(instructorAttendance) 
  //     ? instructorAttendance.filter(record => {
  //         const recordDate = new Date(record.date || record.createdAt).toISOString().split('T')[0];
  //         const isToday = recordDate === today;
  //         return isToday;
  //       })
  //     : [];
  //   
  //   // Try to extract regular attendance from the same response
  //   // Sometimes the instructor attendance might include regular class registrations
  //   const todayRegularAttendance = Array.isArray(instructorAttendance)
  //     ? instructorAttendance.filter(record => {
  //         const recordDate = new Date(record.date || record.createdAt).toISOString().split('T')[0];
  //         // Look for records that don't have instructorClassId (might be regular classes)
  //         const isToday = recordDate === today;
  //         const isRegular = !record.instructorClassId;
  //         return isToday && isRegular;
  //       })
  //     : [];
  //   
  //   const result = {
  //     instructorAttendance: todayInstructorAttendance,
  //     regularAttendance: todayRegularAttendance
  //   };
  //   
  //   return result;
  // } catch (error) {
  //   return {
  //     instructorAttendance: [],
  //     regularAttendance: []
  //   };
  // }
  return {
    instructorAttendance: [],
    regularAttendance: []
  }; // Temporary return for migration
}

export const fetchPendingInstructorAttendance = async () => {
  
  try {
    const res = await apiClient.get(
      `/api/instructor-attendance-pending`,
      { headers: { ...getAuthHeaders() } }
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const approveInstructorAttendance = async (id, studentID, email) => {
  
  try {
    const res = await apiClient.post(
      `/api/instructor-attendance-approve`,
      { 
        attendanceId: id,
        instructorStudentID: studentID,
        instructorEmail: email
      },
      { headers: { ...getAuthHeaders() } }
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const rejectInstructorAttendance = async (id, reason, studentID, email) => {
  
  try {
    const res = await apiClient.post(
      `/api/instructor-attendance-reject`,
      { 
        attendanceId: id, 
        reason: reason,
        instructorStudentID: studentID,
        instructorEmail: email
      },
      { headers: { ...getAuthHeaders() } }
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createClass = async ({ title, dayOfWeek, startTime, endTime, groupId, mat }) => {
  const payload = { title, dayOfWeek, startTime, endTime, groupId, mat };
  const res = await apiClient.post(
  `/api/classes`,
     payload,
    { headers: { ...getAuthHeaders() } }
   );
   return res.data;
};

export const updateClass = async (id, payload) => {
   const res = await apiClient.put(
     `/api/classes/${id}`,
     payload,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
   );
  return res.data;
}
export const deleteClass = async (id) => {
  const res = await apiClient.delete(
     `/api/classes/${id}`,
    { headers: { ...getAuthHeaders() } }
   );
  return res.data;
};

export const createInstructorClass = async ({ title, dayOfWeek, startTime, endTime, groupId, mat }) => {
   const payload = { title, dayOfWeek, startTime, endTime, groupId, mat };
   const res = await apiClient.post(
     `/api/instructor-classes`,
     payload,
     { headers: { ...getAuthHeaders() } }
   );
  return res.data;
};

export const updateInstructorClass = async (id, payload) => {
  const res = await apiClient.put(
     `/api/instructor-classes/${id}`,
     payload,
     { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
   );
   return res.data;
}

export const deleteInstructorClass = async (id) => {
  const res = await apiClient.delete(
     `/api/instructor-classes/${id}`,
    { headers: { ...getAuthHeaders() } }
   );
   return res.data;
};

export const fetchDailyCode = async (date) => {
  return null;
};

export const createDailyCode = async (payload) => {
  return { success: true };
};

export const fetchUserMessages = async (id, readStatus, options = {}) => {
   const params = { id };
   if (readStatus !== undefined && readStatus !== null) {
     params.read = readStatus;
   }
   if (options.limit != null) params.limit = options.limit;
   if (options.offset != null) params.offset = options.offset;
   const response = await apiClient.get(
     `/fetch-user-messages`,
     { params, headers: { ...getAuthHeaders() } }
   );
   return response.data;
};

export const fetchUnreadMessageCount = async (activeStudentID, clubID = null) => {
  try {
    const params = {};
    if (activeStudentID != null) params.studentID = activeStudentID;
    if (clubID != null) params.clubID = clubID;
    const response = await apiClient.get(`/api/messages/unread-count`, {
      params,
      headers: { ...getAuthHeaders() },
      skipSupportErrorReport: true,
    });
    return response.data?.count ?? 0;
  } catch (err) {
    return 0;
  }
};

export const fetchMessagesBySender = async (senderId) => {
   try {
     const response = await apiClient.get(
       `/api/messages-by-sender/${senderId}`,
       { headers: { ...getAuthHeaders() } }
     );
     return response.data;
   } catch (error) {
     // If endpoint doesn't exist, return empty array
     // This allows the feature to work gracefully if backend endpoint is not yet implemented
     console.error('Messages by sender endpoint not available:', error.message);
     return [];
   }
};

export const fetchSenderProfileImage = async (senderId) => {
  try {
    const { data: user } = await apiClient.get(
      `/api/users/${senderId}`,
      { headers: { ...getAuthHeaders() } }
    );
    return {
      name: user?.name || "Unknown Sender",
      studentID: user?.studentID || senderId,
    };
  } catch (err) {
    // 404 is expected when sender no longer exists (deleted user, different ID format, etc.)
    if (err?.response?.status !== 404) {
      console.error(`Error fetching profile image for sender ${senderId}:`, err);
    }
    return {
      name: "Unknown Sender",
      studentID: senderId,
    };
  }
};

export const markMessagesAsRead = async (messageIds, forStudentID = null) => {
  if (!messageIds || messageIds.length === 0) return { success: true, marked: 0 };
  const body = { messageIds };
  if (forStudentID != null) body.forStudentID = forStudentID;
  const response = await apiClient.patch(
    `/api/messages/mark-read`,
    body,
    { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
  );
  return response.data;
};

export const createMessage = async ({
  senderID,
  senderType,
  recipientID,
  recipientType,
  subject = "",
  content,
  status,
  threadId,
}) => {
   const finalThreadId = threadId ?? Math.floor(100000 + Math.random() * 900000);
   const payload = {
     sender: {
       id: senderID,
       type: senderType,       // must be one of ["admin","mentor","user"]
     },
     recipients: [
       {
         id: recipientID,
         type: recipientType,  // same enum
       },
     ],
     subject,
     content,
     status,
     threadId: finalThreadId,
   };

   const response = await apiClient.post(
     `/api/create-message`,
     payload,
     { headers: getAuthHeaders() }
   );
   return response.data;
};

export const massSendMessages = async ({
  senderID, senderType, recipients, subject, content, threadId,
}) => {
  // const messagePromises = recipients.map((recipient) =>
  //   createMessage({
  //     senderID,
  //     senderType,
  //     recipientID: recipient.id,
  //     recipientType: recipient.type,
  //     subject,
  //     content,
  //     threadId,
  //   })
  // );
  // const messages = await Promise.all(messagePromises);
  // return messages;
  return []; // Temporary return for migration
};

export const deleteMessagesByIds = async (ids) => {
   const response = await apiClient.delete(
     `/api/delete-messages`,
     { data: { ids }, headers: { ...getAuthHeaders() } }
   );
   return response.data;
};

export const fetchUsersByCoach = async (coachID, email) => {
  // const response = await apiClient.get(
  //   `/api/users-by-coach`,
  //   { params: { coachID: coachID, email: email }, headers: { ...getAuthHeaders() } }
  // );
  // return response.data;
  return []; // Temporary return for migration
};

export const sendInvoiceToUser = async (member) => {
  // try {
  //   const response = await fetch(
  //     `/api/send-invoice`,
  //     {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         ...getAuthHeaders()
  //       },
  //       body: JSON.stringify({ memberId: member.studentID, memberEmail: member.email }),
  //     }
  //   );
  //   if (!response.ok) {
  //     throw new Error("Failed to send invoice");
  //   }
  // } catch (error) {
  //   console.error("Error sending invoice:", error);
  // }
  return; // Temporary return for migration
};

export const fetchApiMe = async (clubID = null) => {
  try {
    const response = await apiClient.get(`/api/me`, {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching /api/me:", error.response?.data || error.message);
    const payload = error.response?.data;
    if (payload?.error === "club_platform_suspended") {
      const err = new Error(payload.message || "Academy subscription is inactive");
      err.error = "club_platform_suspended";
      err.clubID = payload.clubID;
      throw err;
    }
    throw payload?.error || "Failed to fetch session profile";
  }
};

export const completeMemberProfile = async (payload, clubID = null) => {
  const res = await apiClient.post(
    `/api/complete-profile`,
    payload,
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const completeMemberPersonnummer = async (payload, clubID = null) => {
  const res = await apiClient.post(
    `/api/complete-personnummer`,
    payload,
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
};

export const fetchStudentSportContext = async (studentID, clubID = null) => {
  const response = await apiClient.get(
    `/api/users/${encodeURIComponent(studentID)}/sport-context`,
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const fetchClubSettings = async (clubID = null) => {
  try {
    const response = await apiClient.get(
      `/api/club-settings`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return response.data;
  } catch (error) {
    
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching club settings:", error.response?.data || error.message);
    throw error.response?.data?.error || "Failed to fetch club settings";
  }
};

export const updateClubSettings = async (settingsData, clubID = null) => {
  try {
    const response = await apiClient.put(
      `/api/update-club-settings`,
      settingsData,
      {
        headers: { ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating club settings:",
      error.response?.data || error.message
    );
    throw error.response?.data?.error || "Failed to update club settings";
  }
};

export const fetchColorPromotionEmailSettings = async (clubID = null) => {
  const res = await apiClient.get(`/api/club-settings/color-promotion-emails`, {
    headers: { ...getAuthHeaders() },
    params: mergeClubQueryParams({}, clubID),
  });
  return res.data?.settings ?? res.data;
};

export const updateColorPromotionEmailSettings = async (settings, clubID = null) => {
  const res = await apiClient.put(
    `/api/club-settings/color-promotion-emails`,
    { settings },
    {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data?.settings ?? res.data;
};

/** GDPR Art. 17 erasure request (emailed to NewEra support; logged with reference ID). */
export const submitErasureRequest = async (payload, clubID = null) => {
  try {
    const response = await apiClient.post(`/api/support/erasure-request`, payload, {
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
      skipSupportErrorReport: true,
    });
    return response.data;
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      (error.response?.status === 429
        ? "Too many erasure requests. Please try again later."
        : null) ||
      error.message ||
      "Failed to submit erasure request";
    throw msg;
  }
};

/** Submit in-app bug report (emailed to NewEra support). */
export const submitBugReport = async (payload, clubID = null) => {
  try {
    const appVersion = payload?.appVersion || "";
    const response = await apiClient.post(
      `/api/support/bug-report`,
      payload,
      {
        headers: {
          ...getAuthHeaders(),
          ...(appVersion ? { "X-NewEra-Version": appVersion } : {}),
        },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return response.data;
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      (error.response?.status === 429
        ? "Too many bug reports. Please try again later."
        : null) ||
      error.message ||
      "Failed to submit bug report";
    throw msg;
  }
};

export const createClubSettings = async (settingsData, clubID = null) => {
  try {
    const response = await apiClient.post(
      `/api/create-club-settings`,
      settingsData,
      {
        headers: { ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error creating club settings:",
      error.response?.data || error.message
    );
    throw error.response?.data?.error || "Failed to create club settings";
  }
};

export const fetchClubLogo = async (clubID = null) => {
  // Backend uses req.clubID from JWT, or ?clubID= for admin multi-club
  const params = clubID != null ? { clubID } : {};
  try {
    const response = await apiClient.get(
      `/api/club-logo`,
      { params, headers: { ...getAuthHeaders() } }
    );
    return response.data.logo; // base64 string
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching club logo:", error.response?.data || error.message);
    throw error.response?.data?.error || "Failed to fetch club logo";
  }
};

export const fetchContent = async ({ type = '', page = 1, perPage = 20 } = {}) => {
  
  try {
    const response = await apiClient.get(
      `/api/content`,
      {
        params: { type, page, perPage },
        headers: { ...getAuthHeaders() }
      }
    );
    return response.data;  // array of content items
  } catch (error) {
    console.error('Error fetching content:', error);
    throw error;
  }
};

export const fetchContentById = async (id) => {
  if (!id) throw new Error('Missing content ID');
  
  try {
    const response = await apiClient.get(
      `/api/content/${id}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;  // single content object
  } catch (error) {
    console.error('Error fetching content by ID:', error);
    throw error;
  }
};

export const createContent = async (data, files = [], alts = [], captions = []) => {
  
  try {
    const images = [];
    
    // Upload files directly to Supabase Storage
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = `content/${Date.now()}-${i}-${file.name}`;
      
      try {
        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('content-images')
          .upload(fileName, file, {
            contentType: file.type,
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          continue; // Skip this file and continue with others
        }
        
        // Get public URL
        const { data: urlData } = supabase.storage
          .from('content-images')
          .getPublicUrl(fileName);
        
        images.push({
          url: urlData.publicUrl,
          alt: alts[i] || '',
          caption: captions[i] || '',
          order: i,
          fileName: fileName
        });
      } catch (uploadError) {
        console.error('Error uploading file to Supabase:', uploadError);
        continue; // Skip this file and continue with others
      }
    }
    
    const response = await apiClient.post(
      `/api/content`,
      {
        title: data.title,
        type: data.type,
        body: data.body,
        date: data.date,
        images: images,
        author: data.author || null
      },
      { headers: { ...getAuthHeaders() } }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating content:', error);
    throw error;
  }
};

export const updateContent = async (id, data, files = [], alts = [], captions = []) => {
  
  if (!id) throw new Error("Missing content ID");
  
  try {
    const newImages = [];
    
    // Only upload new files if there are any
    if (files.length > 0) {
      
      // Upload new files directly to Supabase Storage
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `content/${Date.now()}-${i}-${file.name}`;
        
        try {
          // Upload to Supabase Storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('content-images')
            .upload(fileName, file, {
              contentType: file.type,
              upsert: false
            });
          
          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            continue; // Skip this file and continue with others
          }
          
          // Get public URL
          const { data: urlData } = supabase.storage
            .from('content-images')
            .getPublicUrl(fileName);
          
          newImages.push({
            url: urlData.publicUrl,
            alt: alts[i] || '',
            caption: captions[i] || '',
            order: i,
            fileName: fileName
          });
        } catch (uploadError) {
          console.error('Error uploading file to Supabase:', uploadError);
          continue; // Skip this file and continue with others
        }
      }
    }
    
    // Send only new images to backend - let backend handle existing image preservation
    const response = await apiClient.put(
      `/api/content/${id}`,
      {
        title: data.title,
        type: data.type,
        body: data.body,
        date: data.date,
        images: newImages, // Only send new images, not existing ones
        author: data.author || null
      },
      { headers: { ...getAuthHeaders() } }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating content:', error);
    throw error;
  }
};

// ==================== SMILEY MESSAGES API ====================

/**
 * Create a smiley message (green or red)
 * @param {string} studentID - The student who receives the message
 * @param {string} mentorID - The mentor who sends the message
 * @param {string} messageType - 'green' or 'red'
 * @param {string} messageContent - The message content
 * @returns {Promise<Object>} Created message data
 */
export const createSmileyMessage = async (studentID, mentorID, messageType, messageContent) => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  if (!studentID || !mentorID || !messageType || !messageContent) {
    throw new Error('Missing required parameters: studentID, mentorID, messageType, messageContent');
  }
  
  if (!['green', 'red'].includes(messageType)) {
    throw new Error('messageType must be either "green" or "red"');
  }
  
  const payload = {
    studentID,
    mentorID,
    messageType,
    messageContent: messageContent.trim()
  };
  
  const response = await apiClient.post(
    `/api/smiley-messages`,
    payload,
    { headers: { ...getAuthHeaders() } }
  );
  
  return response.data;
};

/**
 * Get all smiley messages for a specific student
 * @param {string} studentID - The student ID
 * @param {string} type - Optional: 'green' or 'red' to filter by type
 * @returns {Promise<Array>} Array of smiley messages
 */
export const getSmileyMessagesByStudent = async (studentID, type = null) => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  if (!studentID) {
    throw new Error('studentID is required');
  }
  
  const params = {};
  if (type && ['green', 'red'].includes(type)) {
    params.type = type;
  }
  
  const response = await apiClient.get(
    `/api/smiley-messages/student/${studentID}`,
    {
      params,
      headers: { ...getAuthHeaders() }
    }
  );
  
  return response.data?.data || [];
};

/**
 * Get smiley message counts for a specific student
 * @param {string} studentID - The student ID
 * @returns {Promise<Object>} Object with { green: number, red: number, total: number }
 */
export const getSmileyMessageCounts = async (studentID) => {
  // Backend automatically enforces clubID from authenticated user's JWT token
  if (!studentID) {
    throw new Error('studentID is required');
  }
  
  const response = await apiClient.get(
    `/api/smiley-messages/counts/${studentID}`,
    {
      headers: { ...getAuthHeaders() }
    }
  );
  
  return response.data?.data || { green: 0, red: 0, total: 0 };
};

/**
 * Get all smiley messages sent by a specific mentor
 * @param {string} mentorID - The mentor ID
 * @param {string} studentID - Optional: filter by student ID
 * @returns {Promise<Array>} Array of smiley messages
 */
export const getSmileyMessagesByMentor = async (mentorID, studentID = null) => {
  if (!mentorID) {
    throw new Error('mentorID is required');
  }
  
  const params = studentID ? { studentID } : {};
  
  const response = await apiClient.get(
    `/api/smiley-messages/mentor/${mentorID}`,
    {
      params,
      headers: { ...getAuthHeaders() }
    }
  );
  
  return response.data?.data || [];
};

/**
 * Delete a smiley message
 * @param {string} messageID - The message ID to delete
 * @returns {Promise<Object>} Success response
 */
export const deleteSmileyMessage = async (messageID) => {
  if (!messageID) {
    throw new Error('messageID is required');
  }
  
  const response = await apiClient.delete(
    `/api/smiley-messages/${messageID}`,
    {
      headers: { ...getAuthHeaders() }
    }
  );
  
  return response.data;
};

export const deleteContent = async (id) => {
  if (!id) throw new Error('Missing content ID');
  
  try {
    const response = await apiClient.delete(
      `/api/content/${id}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting content:', error);
    throw error;
  }
};

// ==================== TRAINING PROGRAMS API ====================

export const fetchTrainingProgramMenuVisibility = async (clubID = null) => {
  try {
    const response = await apiClient.get(
      `/api/training-programs/menu-visibility`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return response.data;
  } catch {
    return { visibleInMenu: true };
  }
};

export const updateTrainingProgramMenuVisibility = async (visibleInMenu, clubID = null) => {
  try {
    const response = await apiClient.patch(
      `/api/training-programs/menu-visibility`,
      { visibleInMenu: Boolean(visibleInMenu) },
      {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        params: mergeClubQueryParams({}, clubID),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating training program menu visibility:', error);
    throw error.response?.data?.error || 'Failed to update menu visibility';
  }
};

export const fetchTrainingPrograms = async ({ page = 1, perPage = 20 } = {}) => {
  try {
    const response = await apiClient.get(
      `/api/training-programs`,
      {
        params: { page, perPage },
        headers: { ...getAuthHeaders() }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching training programs:', error);
    throw error;
  }
};

export const fetchTrainingProgramById = async (id) => {
  if (!id) throw new Error('Missing training program ID');

  try {
    const response = await apiClient.get(
      `/api/training-programs/${id}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching training program by ID:', error);
    throw error;
  }
};

export const createTrainingProgram = async (data, files = [], alts = [], captions = []) => {
  try {
    const images = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = `training-program/${Date.now()}-${i}-${file.name}`;

      try {
        const { error: uploadError } = await supabase.storage
          .from('content-images')
          .upload(fileName, file, {
            contentType: file.type,
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          continue;
        }

        const { data: urlData } = supabase.storage
          .from('content-images')
          .getPublicUrl(fileName);

        images.push({
          url: urlData.publicUrl,
          alt: alts[i] || '',
          caption: captions[i] || '',
          order: i,
          fileName: fileName
        });
      } catch (uploadError) {
        console.error('Error uploading file to Supabase:', uploadError);
        continue;
      }
    }

    const response = await apiClient.post(
      `/api/training-programs`,
      {
        title: data.title,
        body: data.body,
        date: data.date,
        images: images,
        author: data.author || null
      },
      { headers: { ...getAuthHeaders() } }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating training program:', error);
    throw error;
  }
};

export const updateTrainingProgram = async (id, data, files = [], alts = [], captions = []) => {
  if (!id) throw new Error("Missing training program ID");

  try {
    const newImages = [];

    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `training-program/${Date.now()}-${i}-${file.name}`;

        try {
          const { error: uploadError } = await supabase.storage
            .from('content-images')
            .upload(fileName, file, {
              contentType: file.type,
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            continue;
          }

          const { data: urlData } = supabase.storage
            .from('content-images')
            .getPublicUrl(fileName);

          newImages.push({
            url: urlData.publicUrl,
            alt: alts[i] || '',
            caption: captions[i] || '',
            order: i,
            fileName: fileName
          });
        } catch (uploadError) {
          console.error('Error uploading file to Supabase:', uploadError);
          continue;
        }
      }
    }

    const response = await apiClient.put(
      `/api/training-programs/${id}`,
      {
        title: data.title,
        body: data.body,
        date: data.date,
        images: newImages,
        author: data.author || null
      },
      { headers: { ...getAuthHeaders() } }
    );

    return response.data;
  } catch (error) {
    console.error('Error updating training program:', error);
    throw error;
  }
};

export const deleteTrainingProgram = async (id) => {
  if (!id) throw new Error('Missing training program ID');

  try {
    const response = await apiClient.delete(
      `/api/training-programs/${id}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting training program:', error);
    throw error;
  }
};

// ==================== VIDEO LIBRARY API ====================

export const fetchVideoLibraryMenuVisibility = async (clubID = null) => {
  try {
    const response = await apiClient.get(
      `/api/video-library/menu-visibility`,
      { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
    );
    return response.data;
  } catch {
    return { visibleInMenu: true };
  }
};

export const updateVideoLibraryMenuVisibility = async (visibleInMenu, clubID = null) => {
  const response = await apiClient.patch(
    `/api/video-library/menu-visibility`,
    { visibleInMenu: Boolean(visibleInMenu) },
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const fetchVideoLibraryCatalog = async ({ q = '', categoryId = null, tagId = null, clubID = null } = {}) => {
  const params = mergeClubQueryParams(
    {
      ...(q ? { q } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(tagId ? { tagId } : {}),
    },
    clubID
  );
  const response = await apiClient.get(
    `/api/video-library/catalog`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return response.data;
};

export const fetchVideoLibraryAdmin = async ({ q = '', categoryId = null, tagId = null, clubID = null } = {}) => {
  const params = mergeClubQueryParams(
    {
      ...(q ? { q } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(tagId ? { tagId } : {}),
    },
    clubID
  );
  const response = await apiClient.get(
    `/api/video-library/admin`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return response.data;
};

export const createVideoLibraryCategory = async (payload, clubID = null) => {
  const response = await apiClient.post(
    `/api/video-library/categories`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const updateVideoLibraryCategory = async (id, payload, clubID = null) => {
  const response = await apiClient.patch(
    `/api/video-library/categories/${encodeURIComponent(id)}`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const deleteVideoLibraryCategory = async (id, clubID = null) => {
  const response = await apiClient.delete(
    `/api/video-library/categories/${encodeURIComponent(id)}`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const createVideoLibraryTag = async (payload, clubID = null) => {
  const response = await apiClient.post(
    `/api/video-library/tags`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const deleteVideoLibraryTag = async (id, clubID = null) => {
  const response = await apiClient.delete(
    `/api/video-library/tags/${encodeURIComponent(id)}`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

export const createVideoLibraryVideo = async (payload, clubID = null) => {
  const response = await apiClient.post(
    `/api/video-library/videos`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const updateVideoLibraryVideo = async (id, payload, clubID = null) => {
  const response = await apiClient.patch(
    `/api/video-library/videos/${encodeURIComponent(id)}`,
    payload,
    {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return response.data;
};

export const deleteVideoLibraryVideo = async (id, clubID = null) => {
  const response = await apiClient.delete(
    `/api/video-library/videos/${encodeURIComponent(id)}`,
    { headers: { ...getAuthHeaders() }, params: mergeClubQueryParams({}, clubID) }
  );
  return response.data;
};

// ===== NEW ATTENDANCE API FUNCTIONS =====

// Fetch all attendance data for a user
export async function fetchUserAttendance(studentID, filters = {}) {
   const queryParams = new URLSearchParams();
   
   if (filters.startDate) queryParams.append('startDate', filters.startDate);
   if (filters.endDate) queryParams.append('endDate', filters.endDate);
   if (filters.classType && filters.classType !== 'all') queryParams.append('classType', filters.classType);
   if (filters.className) queryParams.append('className', filters.className);
   if (filters.mat) queryParams.append('mat', filters.mat);
   if (
     filters.billingPeriodId &&
     String(filters.billingPeriodId).trim() &&
     String(filters.billingPeriodId).toLowerCase() !== 'all'
   ) {
     queryParams.append('billingPeriodId', String(filters.billingPeriodId).trim());
   }

   const res = await apiClient.get(
     `/api/user-attendance/${studentID}?${queryParams.toString()}`,
     { headers: { ...getAuthHeaders() } }
  );
  return res.data;
}

// Fetch attendance statistics for a user
export async function fetchUserAttendanceStats(studentID, startDateOrOptions = null, endDate = null) {
   const opts =
     typeof startDateOrOptions === 'object' && startDateOrOptions !== null
       ? startDateOrOptions
       : { startDate: startDateOrOptions, endDate };
   const { startDate, endDate: endDateParam, billingPeriodId } = opts;
   const endDateResolved = endDateParam ?? endDate;

   const queryParams = new URLSearchParams();
   if (startDate) queryParams.append('startDate', startDate);
   if (endDateResolved) queryParams.append('endDate', endDateResolved);
   if (
     billingPeriodId &&
     String(billingPeriodId).trim() &&
     String(billingPeriodId).toLowerCase() !== 'all'
   ) {
     queryParams.append('billingPeriodId', String(billingPeriodId).trim());
   }

   const res = await apiClient.get(
     `/api/user-attendance-stats/${studentID}?${queryParams.toString()}`,
     { headers: { ...getAuthHeaders() } }
   );
   return res.data;
}

// Fetch recent check-ins for a user
export async function fetchUserRecentCheckIns(studentID, limitOrOptions = 10) {
   const options =
     typeof limitOrOptions === 'object' && limitOrOptions !== null
       ? limitOrOptions
       : { limit: limitOrOptions };
   const { limit = 10, billingPeriodId } = options;
   const queryParams = new URLSearchParams();
   queryParams.append('limit', String(limit));
   if (
     billingPeriodId &&
     String(billingPeriodId).trim() &&
     String(billingPeriodId).toLowerCase() !== 'all'
   ) {
     queryParams.append('billingPeriodId', String(billingPeriodId).trim());
   }
   const res = await apiClient.get(
     `/api/user-recent-checkins/${studentID}?${queryParams.toString()}`,
     { headers: { ...getAuthHeaders() } }
   );
   return res.data;
}

// ===== ACHIEVEMENTS API FUNCTIONS =====

// Fetch all achievements for a student
export const fetchAchievements = async (studentID) => {
  try {
    const response = await apiClient.get(
      `/api/achievements/${studentID}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data.achievements || [];
  } catch (error) {
    console.error('Error fetching achievements:', error);
    throw error;
  }
};

// Create a new achievement
export const createAchievement = async (achievementData) => {
  try {
    const response = await apiClient.post(
      `/api/achievements`,
      achievementData,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating achievement:', error);
    throw error;
  }
};

// Update an achievement
export const updateAchievement = async (achievementID, achievementData) => {
  try {
    const response = await apiClient.put(
      `/api/achievements/${achievementID}`,
      achievementData,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating achievement:', error);
    throw error;
  }
};

// Delete an achievement
export const deleteAchievement = async (achievementID) => {
  try {
    const response = await apiClient.delete(
      `/api/achievements/${achievementID}`,
      { headers: { ...getAuthHeaders() } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting achievement:', error);
    throw error;
  }
};

// Fetch user drawer stats (admin only)
export async function fetchUserDrawerStats(studentID, options = {}) {
  // const { startDate, endDate } = options;
  // 
  // const queryParams = new URLSearchParams();
  // if (startDate) queryParams.append('startDate', startDate);
  // if (endDate) queryParams.append('endDate', endDate);

  // const res = await apiClient.get(
  //   `/api/admin/user-drawer-stats/${studentID}?${queryParams.toString()}`,
  //   { headers: { ...getAuthHeaders() } }
  // );
  // return res.data;
  return {}; // Temporary return for migration
}

// Fetch comprehensive attendance stats for admin dashboard (ClubAttendancePage)
export async function fetchAdminAttendanceStats(options = {}) {
  const {
    groupFilter,
    classFilter,
    userFilter,
    startDate,
    endDate,
    page = 1,
    limit = 50,
    clubID,
    classTypeFilter,
    search,
    billingPeriodId,
  } = options;

  // Pass clubID for explicit club-scoped data (admins); backend enforceClubID uses it
  const queryParams = new URLSearchParams();
  if (clubID != null) queryParams.append('clubID', clubID);
  if (groupFilter) queryParams.append('groupFilter', groupFilter);
  if (classFilter) queryParams.append('classFilter', classFilter);
  if (userFilter) queryParams.append('userFilter', userFilter);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  if (page) queryParams.append('page', page);
  if (limit) queryParams.append('limit', limit);
  if (classTypeFilter && classTypeFilter !== 'all') {
    queryParams.append('classTypeFilter', classTypeFilter);
  }
  if (search && String(search).trim()) {
    queryParams.append('search', String(search).trim());
  }
  if (billingPeriodId && String(billingPeriodId).trim() && String(billingPeriodId).toLowerCase() !== 'all') {
    queryParams.append('billingPeriodId', String(billingPeriodId).trim());
  }

  const url = `/api/admin/attendance-stats?${queryParams.toString()}`;
  const headers = { ...getAuthHeaders() };

  try {
    const res = await apiClient.get(url, { headers });
    return res.data;
  } catch (error) {
    console.error("Error fetching admin attendance stats:", error);
    throw error;
  }
}

export async function deleteAttendanceRecord(id, type) {
  const url = `/api/admin/attendance-record/${id}?type=${type === "instructor" ? "instructor" : "regular"}`;
  const headers = { ...getAuthHeaders() };
  try {
    const res = await apiClient.delete(url, { headers });
    return res.data;
  } catch (error) {
    console.error("Error deleting attendance record:", error);
    throw error;
  }
}

export async function fetchAttendanceClassNames(clubID) {
  const params = mergeClubQueryParams({}, clubID);
  const res = await apiClient.get(
    `/api/admin/attendance-stats/class-names`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return res.data;
}

export async function renameAttendanceClassName({ fromTitle, toTitle, groupID, classType, clubID } = {}) {
  const res = await apiClient.post(
    `/api/admin/attendance-stats/rename-class`,
    {
      fromTitle,
      toTitle,
      groupID: groupID != null && groupID !== "all" ? groupID : undefined,
      classType: classType && classType !== "all" ? classType : undefined,
    },
    {
      headers: { ...getAuthHeaders() },
      params: mergeClubQueryParams({}, clubID),
    }
  );
  return res.data;
}

export async function fetchDailyDashboard({ date } = {}) {
  const params = mergeClubQueryParams(date ? { date } : {}, null);
  const res = await apiClient.get(
    `/api/dashboard/daily`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return res.data;
}

export async function fetchBirthdaysToday({ date } = {}) {
  const params = mergeClubQueryParams(date ? { date } : {}, null);
  const res = await apiClient.get(
    `/api/birthdays/today`,
    { headers: { ...getAuthHeaders() }, params }
  );
  return res.data;
}